/**
 * Unified tenant profile — modal that expands to full page.
 *
 * Faithful implementation of the handoff prototype (TenantProfile.dc.html):
 * a single tenant view with five tabs (Overview · Payments · Move-in ·
 * Move-out · Documents), a pinned status badge showing the derived 10-stage
 * journey (src/lib/tenantJourney.ts), and a contextual "Next step" action bar.
 *
 * The status layer is the source of truth: each stage's action, when completed,
 * writes the real signal (contract signed, deposit paid, key picked up,
 * move-out scheduled, key returned…) which advances the derived stage
 * automatically. Prototype visuals are matched with inline styles on purpose —
 * this view is pixel-targeted, not theme-driven.
 *
 * Scope note (UI-first pass): lifecycle actions that already have a backend are
 * wired; email sequences bind to the real send-timestamp columns we have and
 * mark the rest as not-yet-sent. See project memory for the full scope.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { usePaymentReceiptsByBooking } from "@/hooks/v2/usePaymentReceipts";
import { supabase } from "@/lib/supabase";
import type { Json } from "@/lib/supabase-types";
import { openProofInNewTab, proofBucket } from "@/lib/proof-url";
import { removeStoredFile, storageRefFor } from "@/lib/storage-url";
import { NotesDrawer } from "@/components/admin/v2/notes/NotesDrawer";
import {
  useOpenNoteCount,
  useRenameNoteAttachment,
} from "@/hooks/v2/useEntityNotes";
import {
  useAddAdminNote,
  useAdminNotes,
  useContractInternalNote,
  useSaveContractInternalNote,
  useAuditLogsForBooking,
  useKeynestInheritance,
  resolveRoomKeynest,
  useBooking,
  useProfile,
  useBookingPayments,
  useHandoverProtocolBundles,
  useIssueComments,
  usePaymentProofsForBooking,
  usePaymentsActivity,
  useBookingPaymentNotes,
  useTenantDocuments,
  useTenantIssues,
  type BookingRow,
} from "@/hooks/v2/useProperties";
import { useTenantSepaStatus } from "@/hooks/v2/usePaymentsAdmin";
import {
  useLatestMoveOutDateRequestForBooking,
  useMoveOutDateRequestForBooking,
} from "@/hooks/v2/useMoveOutDateRequest";
import {
  depositSettlementKey,
  useDepositSettlement,
} from "@/hooks/v2/useDepositSettlement";
import {
  getBundleSignedUrl,
  useAssembleMoveoutBundle,
  usePreviewMoveoutBundleEmail,
  useSendMoveoutBundle,
} from "@/hooks/v2/useMoveoutBundle";
import { SendBundleDialog } from "./SendBundleDialog";
import { SettleDepositDialog } from "./SettleDepositDialog";
import {
  getSignedUrl as getProtocolSignedUrl,
  useAdminProtocolBundle,
  useFinalizeProtocol,
  ReplaceProtocolError,
  useRecordProtocolOnBehalf,
  useRefileProtocolDocument,
} from "@/hooks/useProtocol";
import {
  RecordProtocolDialog,
  type RecordProtocolSubmit,
} from "@/components/admin/v2/tenant/RecordProtocolDialog";
import { generateProtocolPdf } from "@/lib/protocol-pdf";
import { bundleStateOf } from "@/lib/depositSettlement";
import {
  markRefundPaid,
  type DepositReleaseLogEvent,
} from "@/lib/depositReleaseApi";
import { formatDate, formatDateTime, formatEUR } from "@/lib/v2-format";
import {
  depositTopUpCents,
  getExpectedDepositEUR,
  isPaymentEffectivelyPaid,
  ordinalLabel,
  resolveDepositState,
} from "@/lib/rent";
import {
  assertKickbackMonthOpen,
  RentKickbackClosedError,
} from "@/lib/rent-kickback-guard";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { reportOwnerPaymentNotification } from "@/lib/owner-payment-notify-report";
import { manualReminderStage } from "@/lib/rent-reminder-templates";
import { currentPeriodLedger } from "@/lib/rent-past-months";
import { PROPERTY_RENT_PAID_STATS_KEY } from "@/hooks/v2/usePropertyRentPaidStats";
import {
  resolveTenantJourney,
  tenantJourneyStageMeta,
  tenantJourneyStageIndex,
  tenantJourneyStageLabel,
  hasContractDocument,
  bookingColumnsForStage,
  STAGE_FORCE_SPECS,
  TENANT_JOURNEY_STAGES,
  type TenantJourneyStageId,
} from "@/lib/tenantJourney";
import { tenantJourneySignalsFrom } from "@/lib/tenantJourneySignals";
import { TenantArchivedChip } from "@/components/admin/v2/tenant/TenantJourneyStageBadge";
import { TenantEditPanel } from "./TenantEditPanel";
import { ReassignRoomDialog } from "./ReassignRoomDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { actionBtn } from "@/components/admin/v2/tenant/tenant-profile-ui";
import { formatDateNumeric } from "@/lib/date-format";
import {
  OverviewTab,
  PaymentsTab,
  MoveInTab,
  MoveOutTab,
  DocumentsTab,
} from "./tenant-profile-tabs";
import { useTenantFiledDocuments } from "@/hooks/v2/useApartmentDocuments";
import {
  useTenantDocumentLabels,
  useCreateTenantDocumentLabel,
} from "@/hooks/v2/useTenantDocumentLabels";
import { CONTRACT_DOC_ID, resolveDocTarget } from "./resolveDocTarget";
import { MoveOutRejectComposer } from "./MoveOutRejectComposer";
import { CHIP, SectionIcon, UndoBanner, uppLabel } from "./tenant-profile-ui";
import {
  generateProtocolRef,
  isAdminRecorded,
  isRealSubmission,
  isSupersededByReopen,
  PROTOCOL_STORAGE_BUCKET,
} from "@/lib/protocol";
import { buildRecordedProtocolPdf } from "@/lib/protocol-admin-pdf";
import { isMissingColumn } from "@/hooks/v2/useContractFlowReady";
import { useAuth } from "@/contexts/AuthContext";
import { reopenProtocolPatch } from "@/lib/protocol-reopen";
import { SubmitProtocolOnBehalfDialog } from "./SubmitProtocolOnBehalfDialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { contractRefusalMessage } from "@/lib/adminCreateContract";
import { operatorStamped } from "@/lib/operator-stamped";
import { invalidateOccupancyViews } from "@/hooks/v2/useProperties";

/* ------------------------------------------------------------------ */
/* types                                                               */
/* ------------------------------------------------------------------ */

/** The rich tenant object AdminTenants already assembles per booking. */
export interface TenantProfileTenant {
  id: number;
  userId: string | null;
  booking: BookingRow & Record<string, any>;
  profile?: {
    id?: string;
    name?: string | null;
    full_name?: string | null;
    email?: string | null;
    phone?: string | null;
    avatar_url?: string | null;
    date_of_birth?: string | null;
    gender?: string | null;
    nationality?: string | null;
  } | null;
  room?: Record<string, any> | null;
  property?: Record<string, any> | null;
  depositPaid?: boolean;
  guestName?: string | null;
  guestEmail?: string | null;
  guestPhone?: string | null;
}

type TabId = "overview" | "payments" | "movein" | "moveout" | "documents";

const TABS: { id: TabId; labelKey: string; label: string }[] = [
  {
    id: "overview",
    labelKey: "admin.tenantProfile.tabOverview",
    label: "Overview",
  },
  {
    id: "payments",
    labelKey: "admin.tenantProfile.tabPayments",
    label: "Payments",
  },
  { id: "movein", labelKey: "admin.tenantProfile.tabMoveIn", label: "Move-in" },
  {
    id: "moveout",
    labelKey: "admin.tenantProfile.tabMoveOut",
    label: "Move-out",
  },
  {
    id: "documents",
    labelKey: "admin.tenantProfile.tabDocuments",
    label: "Documents",
  },
];

/* ------------------------------------------------------------------ */
/* component                                                           */
/* ------------------------------------------------------------------ */

export function TenantProfileDialog({
  tenant,
  open,
  onOpenChange,
  isSuperAdmin = false,
  canManageTenant = false,
  currentUserId = null,
  onGenerateContract,
  onSendContract,
  initialExpanded = false,
  initialTab,
  backLabel,
  onTabChange,
  headerActions,
  banner,
}: {
  tenant: TenantProfileTenant | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSuperAdmin?: boolean;
  /**
   * Admin or super-admin: may edit the tenant record and drive booking
   * lifecycle actions. Excludes tech, mirroring the SQL `has_role(uid,'admin')`
   * predicate. For the booking-status and date-of-birth controls that predicate
   * is also enforced by a DB trigger, so leaving them visible to tech would
   * surface a raw DB error; the panel's other fields are gated here only.
   */
  canManageTenant?: boolean;
  currentUserId?: string | null;
  /**
   * Reuses the parent's contract-generation flow (create-contract edge fn).
   * `draft: true` generates for review only — no email, tenant sees nothing.
   */
  onGenerateContract?: (
    bookingId: number,
    opts: { templateId?: string | null; draft?: boolean }
  ) => Promise<void>;
  /**
   * Releases an already-verified draft to the tenant (status flip + signing
   * email). Separate from generation on purpose: this is the only action that
   * exposes a contract, so it is the only one behind a confirmation screen.
   */
  onSendContract?: (bookingId: number) => Promise<void>;
  /** Start in full-page (expanded) mode — used by the full-page tenant route. */
  initialExpanded?: boolean;
  /** Deep-link the initial tab (e.g. `?tab=payments`). Defaults to Overview. */
  initialTab?: TabId;
  /** When set, the header shows a "‹ {backLabel}" back-link (route mode). */
  backLabel?: string;
  /** Fires on tab switch so the route can persist it to the URL (`?tab=`). */
  onTabChange?: (tab: TabId) => void;
  /**
   * Extra controls for the header's top-right cluster, rendered before the
   * notes/expand/close buttons (e.g. the tenants table's "…" row actions).
   */
  headerActions?: React.ReactNode;
  /**
   * Full-width row above the header, inside the panel — the panel covers the
   * admin nav, so anything that must stay reachable goes here (route mode:
   * the colivIQ Brain return strip).
   */
  banner?: React.ReactNode;
}) {
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();
  const bookingId = tenant?.id ?? null;
  const userId = tenant?.userId ?? null;

  // Local, optimistic copy so lifecycle actions reflect immediately without a
  // round-trip through the parent's selectedTenant state.
  // Source of truth: the LIVE booking row (so edits made anywhere — the edit
  // panel, other surfaces — reflect here after their query invalidates), with a
  // thin optimistic overlay so a just-completed lifecycle mark shows instantly
  // without waiting for the refetch. The overlay is cleared only when a
  // different tenant is opened.
  const [optimistic, setOptimistic] = useState<Record<string, any>>({});
  const [depositPaidOverride, setDepositPaidOverride] = useState<
    boolean | null
  >(null);
  const [lastUndo, setLastUndo] = useState<{
    label: string;
    fn: () => Promise<void>;
    // When set, the Undo banner renders next to that action's own section
    // instead of the dialog-level banner below.
    anchor?: "movein" | "moveout";
  } | null>(null);
  // Errors surface as toasts, but those render behind the full-screen overlay —
  // so keep the last error visible inline inside the dialog too.
  const [actionErr, setActionErr] = useState<string | null>(null);
  useEffect(() => {
    setOptimistic({});
    setDepositPaidOverride(null);
    setLastUndo(null);
    setActionErr(null);
  }, [tenant?.id]);

  const [tab, setTab] = useState<TabId>(initialTab ?? "overview");
  const changeTab = (tabId: TabId) => {
    setTab(tabId);
    onTabChange?.(tabId);
  };
  const [expanded, setExpanded] = useState(initialExpanded);
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Dunning-reminder confirmation (in-app dialog, not a native confirm()).
  const [reminderStage, setReminderStage] = useState<number | null>(null);
  /** Rent row pending the t("admin.tenantProfile.actionCollectViaDebit", "Collect via direct debit") confirmation (feedback 5a). */
  const [sepaChargeRow, setSepaChargeRow] = useState<any | null>(null);
  /** Mandate-cancellation request awaiting the in-app APPROVE confirm (feedback 6). */
  const [ddCancelApprove, setDdCancelApprove] = useState<string | null>(null);
  /** Checkout protocol: move-out date request awaiting the in-app confirm. */
  const [moveOutRequestConfirm, setMoveOutRequestConfirm] = useState<
    string | null
  >(null);
  /** Checkout protocol: move-out date request id mid-rejection (composer open). */
  const [moveOutRequestReject, setMoveOutRequestReject] = useState<
    string | null
  >(null);
  // Payment-proof deletion confirmation (in-app destructive dialog).
  const [proofToDelete, setProofToDelete] = useState<{
    id: string;
    file_path?: string | null;
  } | null>(null);
  // Bumped every time the dialog opens; used to remount the tab content so all
  // collapsible sections inside the tabs reset to their (collapsed) defaults —
  // a profile always reopens cleanly (feedback #14).
  const [openNonce, setOpenNonce] = useState(0);
  // Reset transient UI (edit panel) only when the dialog opens or the tenant
  // changes — NOT on every tab switch. In route mode a tab switch writes
  // `?tab=` which changes `initialTab`; keeping that out of these deps means a
  // super-admin mid-edit who clicks another tab doesn't lose their form state.
  useEffect(() => {
    if (open) {
      setEditOpen(false);
      setOpenNonce((n) => n + 1);
    }
  }, [open, tenant?.id]);
  // Sync the active tab from the deep-link separately.
  useEffect(() => {
    if (open) setTab(initialTab ?? "overview");
  }, [open, tenant?.id, initialTab]);

  const { data: liveBooking } = useBooking(bookingId);
  /**
   * `tenant` is a snapshot the list captured when the row was clicked, so its
   * `.profile` never changes while the modal is open — an edit to the name,
   * email, phone or date of birth showed only after a page reload, because the
   * identity block reads the profile before it falls back to the booking.
   * Reading the row live fixes that the same way `liveBooking` already does
   * for booking fields, and the update mutation already invalidates this key.
   */
  const { data: liveProfile } = useProfile(userId);
  const { data: realPayments = [], isLoading: paymentsLoading } =
    useBookingPayments(bookingId);
  const { data: realProtocols = [] } = useHandoverProtocolBundles(bookingId);
  const {
    data: realDocuments = [],
    isLoading: documentsLoading,
    isError: documentsFailed,
  } = useTenantDocuments(userId, bookingId);
  const { data: realProofs = [], isLoading: proofsLoading } =
    usePaymentProofsForBooking(bookingId);
  const { data: issues = [] } = useTenantIssues(userId);
  const { data: issueComments = [] } = useIssueComments(
    issues.map((i: any) => i.id)
  );
  const { data: paymentsActivity = [] } = usePaymentsActivity(bookingId);
  // Tenant-scoped admin notes — their file attachments surface in Documents.
  const { data: adminNotes = [] } = useAdminNotes({ bookingId, userId });
  // Renaming one of those attachments writes admin_notes, so it goes through
  // the notes hook — which also refreshes the drawer showing the same file.
  const renameAttachment = useRenameNoteAttachment({
    level: "tenant",
    bookingId,
    userId,
  });
  // Degrades to "no settlement" on a database without the settlement migration,
  // rather than erroring the move-out tab — see useDepositSettlement.
  const { data: depositSettlement } = useDepositSettlement(bookingId);
  const assembleBundle = useAssembleMoveoutBundle(bookingId);

  /**
   * Assemble the move-out bundle. A blocked result is reported as a warning with
   * the missing document named, not as a failure — the admin can act on
   * "move-out protocol has no PDF" and cannot act on "something went wrong".
   */
  const runAssembleBundle = async () => {
    try {
      const result = await assembleBundle.mutateAsync();
      if (result.already_sent || result.already_released) {
        // Not a retryable problem — a refusal to replace a document someone is
        // relying on.
        toast.info(
          result.error ||
            t(
              "admin.tenantProfile.toastBundleLocked",
              "This bundle can no longer be replaced"
            )
        );
        return;
      }
      if (result.blocked) {
        toast.warning(
          result.error ||
            t(
              "admin.tenantProfile.toastBundleMissingDoc",
              "Bundle is missing a document"
            ),
          {
            description: t(
              "admin.tenantProfile.toastBundleNothingSent",
              "Nothing was sent. Fix the document and try again."
            ),
          }
        );
        return;
      }
      const mb = result.size_bytes
        ? `${(result.size_bytes / (1024 * 1024)).toFixed(1)} MB`
        : null;
      await logAudit({
        action: "moveout_bundle_assembled",
        resourceType: "booking",
        resourceId: String(bookingId),
        oldValues: null,
        newValues: {
          page_count: result.page_count ?? null,
          size_bytes: result.size_bytes ?? null,
        },
      });
      toast.success(
        t(
          "admin.tenantProfile.toastBundlePrepared",
          "Move-out bundle prepared"
        ),
        {
          description: [
            result.page_count ? `${result.page_count} pages` : null,
            mb,
          ]
            .filter(Boolean)
            .join(" · "),
        }
      );
      if (result.recipient_missing) {
        // The lessor record has no email. Surfaced now rather than at send time,
        // when it would block the thing the admin actually came to do.
        toast.warning(
          t(
            "admin.tenantProfile.toastNoLessorEmail",
            "No email on file for the lessor's representative"
          ),
          {
            description: t(
              "admin.tenantProfile.toastNoLessorEmailDesc",
              "Add one before the bundle can be sent."
            ),
          }
        );
      }
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("admin.tenantProfile.toastBundleFailed", "Bundle assembly failed")
      );
    }
  };

  // Records a decision the representative gave by phone, under the admin's name.
  const [settleOpen, setSettleOpen] = useState(false);
  const [submitOnBehalfKind, setSubmitOnBehalfKind] = useState<
    "move_in" | "move_out" | null
  >(null);

  /**
   * Send the owner their completion notice, preview first.
   *
   * The mail, its recipient resolution and its bundle attachment all live in
   * _shared/deposit-refund-owner-email.ts; this only drives the dialog. It is a
   * RESEND — confirming the refund mails the owner by itself — so it is reached
   * only from a settled deposit.
   */
  const [sendBundleOpen, setSendBundleOpen] = useState(false);
  const [bundleRecipient, setBundleRecipient] = useState("");
  const previewBundleEmail = usePreviewMoveoutBundleEmail();
  const sendBundle = useSendMoveoutBundle(bookingId);

  /**
   * The address the current preview was actually built for.
   *
   * The Send button must never be live against a preview built for a different
   * recipient — the point of preview-then-send is that what the admin approves is
   * what goes out. So this is compared to what is typed, and Send waits for the
   * preview to catch up.
   */
  const [previewedRecipient, setPreviewedRecipient] = useState<string | null>(
    null
  );

  const openSendBundleDialog = () => {
    const settlement = depositSettlement?.settlement;
    if (!settlement?.id) return;

    // Deliberately NOT seeded from `bundle_recipient_email`. That column holds
    // the LESSOR'S REPRESENTATIVE — whoever the review bundle was sent to — and
    // this mail goes to the APARTMENT OWNER, who is a different record and
    // often a different person. Seeding from it would send an owner's
    // completion notice to the representative and, because any typed address
    // becomes an override, would skip the owner lookup entirely.
    //
    // Only the server can resolve the owner (portal account, else effective
    // lessor), so the dialog opens with no override and takes the address back
    // out of the preview it gets. An owner with no address on file comes back
    // as a 409 the dialog shows, and the admin can type one — which is the case
    // this whole editable field exists for.
    setBundleRecipient("");
    setPreviewedRecipient(null);
    setSendBundleOpen(true);
    previewBundleEmail.reset();
    previewBundleEmail.mutate(
      { settlementId: settlement.id, mode: "owner" },
      {
        onSuccess: (result) => {
          const resolved = result?.preview?.to?.[0] ?? "";
          setBundleRecipient(resolved);
          setPreviewedRecipient(resolved.trim() || null);
        },
      }
    );
  };

  /**
   * Rebuild the preview when the typed recipient changes.
   *
   * Without this the dialog dead-ends on its own headline case: a lessor with no
   * email on file gets a 409 on open, `preview` stays null, and Send is gated on
   * `preview` — so an admin could type a correct address and the button would
   * never unlock. Debounced so it does not fire per keystroke.
   */
  const settlementIdForPreview = depositSettlement?.settlement?.id ?? null;
  useEffect(() => {
    if (!sendBundleOpen || !settlementIdForPreview) return;
    const typed = bundleRecipient.trim();
    if (!typed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typed)) return;
    if (typed === previewedRecipient) return;

    const timer = setTimeout(() => {
      previewBundleEmail.mutate(
        {
          settlementId: settlementIdForPreview,
          recipientEmail: typed,
          mode: "owner",
        },
        { onSuccess: () => setPreviewedRecipient(typed) }
      );
    }, 400);
    return () => clearTimeout(timer);
    // previewBundleEmail is a stable mutation object; including it would refire
    // on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sendBundleOpen,
    settlementIdForPreview,
    bundleRecipient,
    previewedRecipient,
  ]);

  const submitSendBundle = async () => {
    const settlement = depositSettlement?.settlement;
    if (!settlement?.id) return;
    try {
      await sendBundle.mutateAsync({
        settlementId: settlement.id,
        recipientEmail: bundleRecipient.trim() || null,
        mode: "owner",
      });
      setSendBundleOpen(false);
      await logAudit({
        action: "moveout_bundle_sent",
        resourceType: "deposit_settlement",
        resourceId: String(settlement.id),
        oldValues: null,
        newValues: { recipient: bundleRecipient.trim() || null },
      });
      toast.success(
        t(
          "admin.tenantProfile.toastOwnerConfirmationSent",
          "Confirmation sent to the owner"
        ),
        {
          description: `${bundleRecipient.trim()} · your admins were copied`,
        }
      );
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("admin.tenantProfile.toastSendFailed", "Send failed")
      );
    }
  };

  // ── refund payout ──
  const [markingRefundPaid, setMarkingRefundPaid] = useState(false);

  /**
   * Attest that the transfer has gone out. The money moves by bank transfer
   * outside this system, so this records the fact rather than causing it — same
   * shape as marking rent paid.
   */
  const runMarkRefundPaid = async () => {
    const settlement = depositSettlement?.settlement;
    if (!settlement?.id) return;
    setMarkingRefundPaid(true);
    try {
      await markRefundPaid({ mode: "admin", settlementId: settlement.id });
      await queryClient.invalidateQueries({
        queryKey: depositSettlementKey(bookingId),
      });
      await logAudit({
        action: "deposit_refund_marked_paid",
        resourceType: "deposit_settlement",
        resourceId: String(settlement.id),
        oldValues: null,
        newValues: { refund_paid: true },
      });
      toast.success(
        t("admin.tenantProfile.toastRefundPaid", "Refund marked as paid")
      );
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t(
              "admin.tenantProfile.toastRefundFailed",
              "Could not record the payment"
            )
      );
    } finally {
      setMarkingRefundPaid(false);
    }
  };

  const openStatement = async () => {
    const path = depositSettlement?.settlement?.statement_pdf_path;
    if (!path) return;
    const url = await getBundleSignedUrl(path);
    if (!url) {
      toast.error(
        t(
          "admin.tenantProfile.toastStatementOpenFailed",
          "Could not open the statement."
        )
      );
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openBundle = async () => {
    const path = depositSettlement?.settlement?.bundle_pdf_path;
    if (!path) return;
    const url = await getBundleSignedUrl(path);
    if (!url) {
      toast.error(
        t(
          "admin.tenantProfile.toastBundleOpenFailed",
          "Could not open the bundle. Try preparing it again."
        )
      );
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };
  /**
   * Shared by the "Settle deposit…" dialog and the inline panel in the Move-out
   * tab: both write through the same endpoint, so both need the same refetch.
   */
  const onDepositReleased = () => {
    toast.success(
      t(
        "admin.tenantProfile.toastDepositDecisionRecorded",
        "Deposit decision recorded"
      )
    );
    void queryClient.invalidateQueries({
      queryKey: depositSettlementKey(bookingId),
    });
  };

  /**
   * Turn a fact reported by the deposit panel or the refund form into an
   * audit row, so a deduction added and a decision recorded show up on the
   * Activity Timeline the same way every other tenant-profile action does —
   * see tenant-audit-titles.ts for the fixed titles these actions render as.
   */
  const logDepositEvent = async (event: DepositReleaseLogEvent) => {
    const settlementId = depositSettlement?.settlement?.id;
    if (!settlementId) return;
    const resourceType = "deposit_settlement";
    // Written as three literal branches, not a template built off event.kind,
    // so tenant-audit-titles.test.ts's regex scan (which only matches a bare
    // string literal) actually sees each action string — see its own header
    // comment on why that scan exists.
    if (event.kind === "deduction_added") {
      await logAudit({
        action: "deposit_deduction_added",
        resourceType,
        resourceId: settlementId,
        oldValues: null,
        newValues: {
          category: event.category,
          amount_cents: event.amountCents,
        },
      });
      return;
    }
    if (event.kind === "deduction_updated") {
      await logAudit({
        action: "deposit_deduction_updated",
        resourceType,
        resourceId: settlementId,
        oldValues: null,
        newValues: {
          category: event.category,
          amount_cents: event.amountCents,
        },
      });
      return;
    }
    if (event.kind === "deduction_deleted") {
      await logAudit({
        action: "deposit_deduction_deleted",
        resourceType,
        resourceId: settlementId,
        oldValues: null,
        newValues: {
          category: event.category,
          amount_cents: event.amountCents,
        },
      });
      return;
    }
    // Only the two release kinds get here. TS cannot subtract the three
    // deduction literals from their shared variant, so name what is left.
    const released = event as Extract<
      DepositReleaseLogEvent,
      { refundCents: number }
    >;
    await logAudit({
      action: "deposit_released",
      resourceType,
      resourceId: settlementId,
      oldValues: null,
      newValues: {
        ...(event.kind === "released" ? { decision: event.decision } : {}),
        refund_cents: released.refundCents,
        excess_cents: released.excessCents,
      },
    });
    // The one-step path (a proof attached) both releases and marks the
    // refund paid in the same server call — log the second half too, so it
    // reads the same as the two-step admin path's own explicit entry rather
    // than going quiet on the fact that the money moved.
    if (event.kind === "released_by_amount" && event.paid) {
      await logAudit({
        action: "deposit_refund_marked_paid",
        resourceType,
        resourceId: settlementId,
        oldValues: null,
        newValues: { refund_paid: true },
      });
    }
  };

  /**
   * Save the bundle rather than view it. Same signed URL; the `download`
   * attribute is what makes the browser write it to disk instead of navigating,
   * and the anchor is never attached to the document so nothing flickers.
   */
  const downloadBundle = async () => {
    const path = depositSettlement?.settlement?.bundle_pdf_path;
    if (!path) return;
    const url = await getBundleSignedUrl(path);
    if (!url) {
      toast.error(
        t(
          "admin.tenantProfile.toastBundleDownloadFailed",
          "Could not download the bundle. Try preparing it again."
        )
      );
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = "deposit-review.pdf";
    a.rel = "noopener";
    a.click();
  };

  // Badge on the header's notes button. Same query the drawer reads, so opening
  // it costs nothing extra.
  const openNoteCount = useOpenNoteCount(
    bookingId == null && !userId ? null : { level: "tenant", bookingId, userId }
  );
  const addNote = useAddAdminNote();
  // The admin-only contract note lives in admin_notes (RLS: admins read,
  // super_admins write), NOT on the booking row the tenant can select.
  const { data: contractInternalNote = null } =
    useContractInternalNote(bookingId);
  const saveInternalNote = useSaveContractInternalNote();
  // Audit-log edits (super-admin manual overrides) → t("admin.tenantProfile.toastDetailsUpdated", "Details updated") events
  // in the Activity Timeline, with the editor + timestamp.
  const { data: auditLogs = [] } = useAuditLogsForBooking(bookingId);
  // Notes added against an individual payment row — merged into the Activity
  // Timeline so they don't stay hidden behind that row's Notes toggle.
  const { data: paymentNotes = [] } = useBookingPaymentNotes(bookingId);
  // Notes per payment, so each row's "Notes" button can carry a count instead
  // of hiding the fact that notes exist behind a click.
  const noteCountByPayment = useMemo(() => {
    const m: Record<string, number> = {};
    for (const n of paymentNotes as any[]) {
      const id = String(n?.payment_id ?? "");
      if (!id) continue;
      m[id] = (m[id] ?? 0) + 1;
    }
    return m;
  }, [paymentNotes]);

  // The live booking + optimistic overlay is the working booking for the real
  // (non-preview) view and for all lifecycle reads/writes.
  const booking = useMemo(
    () =>
      ({
        ...(liveBooking ?? tenant?.booking ?? ({} as Partial<BookingRow>)),
        ...optimistic,
      }) as BookingRow & Record<string, any>,
    [liveBooking, tenant?.booking, optimistic]
  );
  // Live row wins field by field, with the snapshot filling anything the
  // profiles select doesn't carry. Merged rather than swapped so the identity
  // block doesn't blank out during the first fetch, or for a booking whose
  // tenant has no profile row at all.
  //
  // Keyed on the VALUES, not the two objects: React Query returns a fresh
  // object from every fetch, and TenantEditPanel re-seeds its inputs whenever
  // the profile it is handed changes identity. Keying on identity would let a
  // background refetch (window focus, an invalidation from elsewhere) wipe
  // half-typed input mid-edit.
  const profileSig = JSON.stringify([
    tenant?.profile ?? null,
    liveProfile ?? null,
  ]);
  const profile = useMemo(() => {
    const [snapshot, live] = JSON.parse(profileSig) as [
      TenantProfileTenant["profile"],
      TenantProfileTenant["profile"],
    ];
    return { ...snapshot, ...live } as NonNullable<
      TenantProfileTenant["profile"]
    >;
  }, [profileSig]);
  // Prune overlay keys the server has caught up on. The overlay only exists to
  // show a just-completed mark before the refetch lands; once `liveBooking`
  // carries the same value, keeping the key would let a stale optimistic value
  // mask newer server state (e.g. an edit made on another surface) and make the
  // edit panel diff against the overlay instead of the DB row.
  useEffect(() => {
    if (!liveBooking) return;
    setOptimistic((o) => {
      const keys = Object.keys(o);
      if (keys.length === 0) return o;
      const next: Record<string, any> = {};
      for (const k of keys) {
        if ((liveBooking as any)[k] !== o[k]) next[k] = o[k];
      }
      return Object.keys(next).length === keys.length ? o : next;
    });
  }, [liveBooking]);
  // COL-417: the receipts behind each payment. Fetched here with the rest of
  // the profile's data and handed down, so PaymentsTab stays a presentational
  // component (it is rendered in tests without a QueryClientProvider).
  const { data: receiptsByPayment = {} } =
    usePaymentReceiptsByBooking(bookingId);

  // Latest proof per payment id (proofs come newest-first).
  const proofByPayment = useMemo(() => {
    const m: Record<string, any> = {};
    for (const pr of realProofs as any[]) {
      if (!m[String(pr.payment_id)]) m[String(pr.payment_id)] = pr;
    }
    return m;
  }, [realProofs]);

  // Deposit signal — the canonical resolveDepositState, identical to the one
  // the tenant list uses, so the journey stage can't differ between the two
  // surfaces. It requires the FULL contractual deposit (a part-payment, or a
  // manual "paid" row with neither a proof nor an admin attestation behind it,
  // is not enough to clear the Deposit-pending gate). While payments OR PROOFS
  // are still loading we fall back to the value the list already computed, so
  // the stage doesn't flicker downward — the predicate reads both queries, so
  // guarding on `paymentsLoading` alone still flashed when proofs resolved last.
  const depositState = useMemo(
    () =>
      resolveDepositState({
        depositPayments: (realPayments as any[]).filter(
          (p) => p?.type === "deposit"
        ),
        isEffectivelyPaid: (p) =>
          isPaymentEffectivelyPaid(
            p as any,
            (id) => proofByPayment[id] ?? null
          ),
        expectedDepositEUR: getExpectedDepositEUR(booking as any),
      }),
    [realPayments, proofByPayment, booking]
  );
  const paymentsDepositPaid = depositState.status === "paid";
  const depositSignalLoading = paymentsLoading || proofsLoading;
  const depositPaid =
    depositPaidOverride ??
    (depositSignalLoading ? !!tenant?.depositPaid : paymentsDepositPaid);

  // A pending file waiting for its mandatory handover note before it uploads.
  const [pendingUpload, setPendingUpload] = useState<{
    file: File;
    kind: "contract" | "key-pickup" | "key-return";
  } | null>(null);

  // ── view data (kept under `view*` aliases to minimise churn) ──
  const viewTenant = tenant;
  const viewBooking = booking;
  const viewDepositPaid = depositPaid;
  const payments = realPayments;
  const protocolBundles = realProtocols;
  const documents = realDocuments;
  /* What the owner (or staff) filed AGAINST this tenancy — apartment_documents
     with scope='tenant'. Its own query rather than part of realDocuments:
     those are tenant_documents rows, a different table with a different
     ownership model, and merging them at the source would blur which of the
     two a rename or delete should act on. */
  const { data: filedDocuments = [] } = useTenantFiledDocuments(bookingId);
  const { data: customDocLabels = [] } = useTenantDocumentLabels();
  const createDocLabel = useCreateTenantDocumentLabel();

  /**
   * A reopen granted AFTER a record was filed retires it — the same rule the
   * tenant's own protocol card applies, via the same `isSupersededByReopen`.
   *
   * Without this the two sides disagreed about the same tenancy: the tenant saw
   * "start your walkthrough" (their card honours the reopen) while this profile
   * still read "submitted" off the retired row, so the admin who reopened it had
   * no way to see that they had. Every `*Submitted` reader below — the journey
   * resolver, the Move-in/Move-out cards, the close-protocol toggles' decision
   * about whether to stamp `manually_closed_at` — hangs off these two flags, so
   * the rule belongs here rather than at each call site.
   *
   * The retired row keeps its PDF and still lists in Documents; it just stops
   * answering "is this kind done?".
   */
  const moveInReopenedAt =
    ((booking as any)?.move_in_protocol_reopened_at as string | null) ?? null;
  const moveOutReopenedAt =
    ((booking as any)?.move_out_protocol_reopened_at as string | null) ?? null;
  const reopenedAtFor = (kind: "move_in" | "move_out") =>
    kind === "move_in" ? moveInReopenedAt : moveOutReopenedAt;
  /** Does this bundle still speak for its kind? */
  const protocolStands = (b: any) =>
    !isSupersededByReopen(b, reopenedAtFor(b.kind));

  const moveInProtocolSubmitted = protocolBundles.some(
    (b: any) =>
      b.kind === "move_in" && b.status === "submitted" && protocolStands(b)
  );
  const moveOutProtocolSubmitted = protocolBundles.some(
    (b: any) =>
      b.kind === "move_out" && b.status === "submitted" && protocolStands(b)
  );
  /**
   * The protocol a card speaks for: the SUBMITTED one when there is one, else
   * the newest of any status.
   *
   * The list arrives newest-first, so a plain find() returned a later draft —
   * a restarted walkthrough — while `*Submitted` above (a .some()) still read
   * true from the older submitted row. The card then said t("admin.tenantProfile.statusSubmitted", "Submitted") and
   * linked to an empty draft, and the PDF regeneration below aimed at the
   * wrong row.
   */
  // A real submission outranks the tick's deemed-accepted placeholder, which
  // also carries status 'submitted'. Without that order, a tenant let back in
  // after an auto-close files the real walkthrough and the card keeps
  // speaking for the placeholder — wrong status, wrong PDF, and the
  // regeneration below aimed at the wrong row.
  //
  // `protocolStands(b)` here too: a real submission a LATER reopen has since
  // superseded must not outrank a fresher/standing row the same way an
  // auto-submitted one wouldn't — clauses below already exclude superseded
  // rows, so this one staying unfiltered was the one gap in "every reader
  // agrees which record stands" for this file.
  const pickProtocol = (kind: "move_in" | "move_out") =>
    protocolBundles.find(
      (b: any) => b.kind === kind && isRealSubmission(b) && protocolStands(b)
    ) ??
    protocolBundles.find(
      (b: any) =>
        b.kind === kind && b.status === "submitted" && protocolStands(b)
    ) ??
    // Fall back to the newest row of any status, superseded ones excluded —
    // otherwise a reopen that retired the only submission would put the card
    // straight back on it and re-assert "Submitted" one line down from the
    // flags above.
    protocolBundles.find((b: any) => b.kind === kind && protocolStands(b)) ??
    protocolBundles.find((b: any) => b.kind === kind);
  const moveInProtocol = pickProtocol("move_in");
  const moveOutProtocol = pickProtocol("move_out");

  /**
   * Admin-side regeneration for a protocol whose PDF never got finalised —
   * the tenant flow does this automatically on submit, but a closed tab or a
   * transient error can leave `pdf_storage_path` empty forever otherwise.
   * Same builder and same finalize path as the tenant flow (DoneStep) and the
   * standalone protocol detail page, so this produces the identical document.
   *
   * The photo/inventory/defect fetch is only enabled while a PDF is actually
   * missing, so a tenant profile with both protocols already finalised never
   * pays for it.
   */
  const moveInNeedsPdf = !!moveInProtocol && !moveInProtocol.pdf_storage_path;
  const moveOutNeedsPdf =
    !!moveOutProtocol && !moveOutProtocol.pdf_storage_path;
  const moveInProtocolBundle = useAdminProtocolBundle(
    moveInNeedsPdf ? moveInProtocol.id : undefined
  );
  const moveOutProtocolBundle = useAdminProtocolBundle(
    moveOutNeedsPdf ? moveOutProtocol.id : undefined
  );
  const finalizeMoveInProtocol = useFinalizeProtocol(moveInProtocol?.id ?? "");
  const finalizeMoveOutProtocol = useFinalizeProtocol(
    moveOutProtocol?.id ?? ""
  );
  const [generatingProtocol, setGeneratingProtocol] = useState<
    "move_in" | "move_out" | null
  >(null);
  const [recordProtocolKind, setRecordProtocolKind] = useState<
    "move_in" | "move_out" | null
  >(null);
  /** Same dialog, opened to swap the document on an operator-recorded protocol. */
  const [replaceProtocolKind, setReplaceProtocolKind] = useState<
    "move_in" | "move_out" | null
  >(null);
  const recordProtocol = useRecordProtocolOnBehalf();
  const refileProtocolDocument = useRefileProtocolDocument();
  /** The operator-recorded protocol a replacement would overwrite, once chosen. */
  const replaceTarget =
    replaceProtocolKind === "move_in"
      ? moveInProtocol
      : replaceProtocolKind === "move_out"
        ? moveOutProtocol
        : null;
  const { user: currentUser } = useAuth();

  const generateProtocolPdfFor = async (kind: "move_in" | "move_out") => {
    const protocol = kind === "move_in" ? moveInProtocol : moveOutProtocol;
    const bundle =
      kind === "move_in"
        ? moveInProtocolBundle.data
        : moveOutProtocolBundle.data;
    const finalize =
      kind === "move_in" ? finalizeMoveInProtocol : finalizeMoveOutProtocol;
    // No row at all: t("admin.tenantProfile.actionGenerate", "Generate") is offered from the blocked deposit card, which
    // is exactly the state where the tenant never started one. Regenerating
    // nothing is impossible, so send the admin where they can actually fix it
    // rather than telling them to wait for a load that will never finish.
    if (!protocol) {
      setRecordProtocolKind(kind);
      return;
    }
    if (!bundle) {
      toast.error(
        t(
          "admin.tenantProfile.toastProtocolLoading",
          "Protocol details are still loading — try again in a moment."
        )
      );
      return;
    }
    setGeneratingProtocol(kind);
    const toastId = toast.loading("Generating PDF…", {
      description: t(
        "admin.tenantProfile.toastProtocolEmbedding",
        "Embedding photos and signature — this may take a moment."
      ),
    });
    try {
      const blob = await generateProtocolPdf({
        protocol: bundle.protocol,
        photos: bundle.photos,
        inventory: bundle.inventory,
        defects: bundle.defects,
        booking: bundle.booking
          ? {
              property_name: bundle.booking.property_name,
              room_number: bundle.booking.room_number,
              address: bundle.booking.address,
              move_in_date: bundle.booking.move_in_date ?? null,
              move_out_date: bundle.booking.move_out_date ?? null,
              deposit_amount: bundle.booking.deposit_amount ?? null,
            }
          : null,
        tenant: {
          name: bundle.booking?.tenant_name ?? null,
          email: bundle.booking?.tenant_email ?? null,
        },
        language: "en",
      });
      await finalize.mutateAsync({
        pdfBlob: blob,
        pdfFilename: `${protocol.ref}.pdf`,
        language: "en",
        existingPdfPath: protocol.pdf_storage_path ?? null,
        // An operator-recorded protocol counts as already-emailed even when it
        // never was: the admin who filed it chose not to notify the tenant, and
        // without this that decision would be quietly reversed weeks later by
        // whoever next clicks Generate.
        alreadyEmailed: !!protocol.email_sent_at || isAdminRecorded(protocol),
      });
      toast.success(
        t(
          "admin.tenantProfile.toastProtocolPdfGenerated",
          "Protocol PDF generated"
        ),
        { id: toastId }
      );
      void queryClient.invalidateQueries({
        queryKey: ["v2", "handover-bundles", bookingId],
      });
    } catch (err: any) {
      toast.error(
        err?.message ??
          t(
            "admin.tenantProfile.toastProtocolPdfFailed",
            "Could not generate the protocol PDF"
          ),
        {
          id: toastId,
        }
      );
    } finally {
      setGeneratingProtocol(null);
    }
  };

  const openProtocolPdf = async (kind: "move_in" | "move_out") => {
    const protocol = kind === "move_in" ? moveInProtocol : moveOutProtocol;
    const path = protocol?.pdf_storage_path;
    if (!path) return;
    const url = await getProtocolSignedUrl(path);
    if (!url) {
      toast.error(
        t(
          "admin.tenantProfile.toastProtocolPdfOpenFailed",
          "Could not open the protocol PDF."
        )
      );
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadProtocolPdfFor = async (kind: "move_in" | "move_out") => {
    const protocol = kind === "move_in" ? moveInProtocol : moveOutProtocol;
    const path = protocol?.pdf_storage_path;
    if (!path) return;
    const url = await getProtocolSignedUrl(path);
    if (!url) {
      toast.error(
        t(
          "admin.tenantProfile.toastProtocolPdfDownloadFailed",
          "Could not download the protocol PDF."
        )
      );
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = `${protocol.ref}.pdf`;
    a.rel = "noopener";
    a.click();
  };

  // Same source as the paid flag above — the latest effectively-paid deposit
  // row's paid_at, not just any row whose status column says "paid".
  const depositPaidAt = depositState.paidAtIso;

  const journey = useMemo(() => {
    /*
     * Signals come from the shared assembly, so this header, the Tenants list,
     * the room view's current-tenant card and the Reminders panel cannot put
     * the same tenancy at different stages.
     *
     * It matters most for a tenancy with NO deposit configured. The header used
     * to feed the resolver `viewDepositPaid`, which is
     * `resolveDepositState(...) === "paid"` — and that reports "due" for a zero
     * expectation, because it cannot tell "waived" from "not set up yet". The
     * profile therefore stranded such a tenancy at Deposit pending while every
     * other surface (on the full-deposit resolver, which treats nothing-owed as
     * satisfied) had already advanced it. The shared helper is the one that is
     * right: there is no deposit to collect that could ever release the gate.
     */
    const signals = tenantJourneySignalsFrom(viewBooking, {
      payments: payments as any[],
      proofFor: (id) => proofByPayment[id] ?? null,
      submittedProtocolKinds: new Set([
        ...(moveInProtocolSubmitted ? ["move_in"] : []),
        ...(moveOutProtocolSubmitted ? ["move_out"] : []),
      ]),
      settlement: depositSettlement?.settlement ?? null,
    });
    return resolveTenantJourney(viewBooking, {
      ...signals,
      // The profile alone carries an admin override and a first-paint fallback
      // from the list row it was opened from; both still win over the flag
      // derived here.
      depositPaid:
        depositPaidOverride ??
        (depositSignalLoading ? !!tenant?.depositPaid : signals.depositPaid),
    });
  }, [
    viewBooking,
    payments,
    proofByPayment,
    moveInProtocolSubmitted,
    moveOutProtocolSubmitted,
    depositSettlement,
    depositPaidOverride,
    depositSignalLoading,
    tenant?.depositPaid,
  ]);

  const name =
    profile?.full_name ||
    profile?.name ||
    viewTenant?.guestName ||
    viewBooking.name ||
    profile?.email ||
    "Tenant";
  const email =
    profile?.email || viewTenant?.guestEmail || viewBooking.email || "—";
  const phone =
    profile?.phone || viewTenant?.guestPhone || viewBooking.phone || "—";
  const initials =
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((p: string) => p[0]?.toUpperCase() ?? "")
      .join("") || "T";

  const property = viewTenant?.property ?? null;
  const room = viewTenant?.room ?? null;
  const propertyTitle = property?.title || "—";
  const roomTitle = room?.title || room?.room_number || "—";
  // Reassign needs concrete room + property ids.
  const reassignRoomId =
    Number(room?.id ?? (viewBooking as any)?.room_id) || null;
  const reassignPropertyId =
    Number(property?.id ?? (viewBooking as any)?.property_id) || null;
  const canReassign = isSuperAdmin && !!reassignRoomId && !!reassignPropertyId;
  // Real SEPA state for the payment-method card — the old placeholder derived
  // t("admin.tenantProfile.mandateActive", "Mandate active") from depositPaid, which lied in production.
  const sepa = useTenantSepaStatus(userId, reassignRoomId);
  // Checkout protocol (Neel Slack DM 2026-10-19).
  const { data: moveOutDateRequest } =
    useMoveOutDateRequestForBooking(bookingId);
  const { data: latestMoveOutDateRequest } =
    useLatestMoveOutDateRequestForBooking(bookingId);
  // KeyNest inherits property → apartment → room. Merge the apartment/property
  // defaults onto the room so the profile's Keys section shows the effective
  // pickup details even when the room itself has none (feedback #16).
  // `bookings` has no apartment_id column — the room carries it.
  const apartmentId = Number((room as any)?.apartment_id) || null;
  const { data: keynestInherit } = useKeynestInheritance(
    reassignPropertyId,
    apartmentId
  );
  const roomWithKeynest = useMemo(
    () => resolveRoomKeynest(room, keynestInherit),
    [room, keynestInherit]
  );
  // Street line (prefer the stored single-line address) + a postal-code/city
  // line. Only append the city line when the street line doesn't already carry
  // the city, so a full stored address ("…12, 86150 Augsburg") isn't doubled.
  const streetLine =
    property?.address ||
    [property?.street_name, property?.street_number].filter(Boolean).join(" ");
  const cityLine = [property?.zip_code, property?.city]
    .filter(Boolean)
    .join(" ");
  const cityAlreadyPresent =
    !!property?.city &&
    streetLine.toLowerCase().includes(String(property.city).toLowerCase());
  const address =
    [streetLine, cityAlreadyPresent ? "" : cityLine]
      .filter(Boolean)
      .join(", ") ||
    property?.city ||
    "";

  /* -------------------- lifecycle actions -------------------- */

  // Refetch relevant caches after a successful write. Fire-and-forget — a
  // failing background refetch must never surface as a failed action (that was
  // the spurious "error" after a mark that actually succeeded).
  function refreshAfterWrite() {
    void invalidateOccupancyViews(queryClient);
    void queryClient.invalidateQueries({ queryKey: ["v2"] });
    // Every payment/proof/rent write in this dialog (mark paid, SEPA charge,
    // proof upload/approve, rent edits) funnels through here — so the
    // properties-grid "% rent paid" pill (PROPERTY_RENT_PAID_STATS_KEY)
    // doesn't sit stale for its 30s staleTime after an admin acts here.
    void queryClient.invalidateQueries({
      queryKey: [PROPERTY_RENT_PAID_STATS_KEY],
    });
  }

  /**
   * Write an audit_logs entry so an edit surfaces in the Activity Timeline.
   * Every row is tagged with `metadata.booking_id` so the timeline query picks
   * it up regardless of the resource_type/resource_id it's filed under (e.g. a
   * payment edit filed under the payment). Best-effort: a failed audit write
   * must never block the primary mutation, so we swallow errors here.
   */
  async function logAudit(entry: {
    action: string;
    resourceType: string;
    resourceId: string;
    oldValues?: Record<string, Json | undefined> | null;
    newValues?: Record<string, Json | undefined> | null;
    metadata?: Record<string, Json | undefined>;
  }) {
    try {
      await supabase.from("audit_logs").insert({
        user_id: currentUserId ?? null,
        action: entry.action,
        resource_type: entry.resourceType,
        resource_id: entry.resourceId,
        old_values: entry.oldValues ?? null,
        new_values: entry.newValues ?? null,
        metadata: {
          ...(entry.metadata ?? {}),
          booking_id: bookingId,
          source: "tenant_profile",
        },
      });
    } catch (e) {
      console.error(
        t(
          "admin.tenantProfile.toastAuditLogFailed",
          "Failed to write audit log"
        ),
        e
      );
    }
  }

  function actionError(e: unknown): string {
    const refusal = contractRefusalMessage(e, t);
    if (refusal) return refusal;
    let msg = t("admin.tenantProfile.toastActionFailed", "Action failed.");
    if (e && typeof e === "object") {
      const o = e as Record<string, any>;
      const parts = [o.message, o.details, o.hint].filter(Boolean);
      if (parts.length) msg = parts.join(" · ");
    } else if (e instanceof Error) {
      msg = e.message;
    }
    // PostgREST answers 400 with "column ... does not exist" when a project
    // hasn't run the contract migrations. That is a deploy step, not a bug in the
    // write — say which one, or an admin just sees "400".
    if (/contract_draft_verif/.test(msg)) {
      msg +=
        " — this database is missing migration 20260803090000_contract_draft_verification. Run `supabase db push` against it.";
    } else if (/kind/.test(msg) && /column/i.test(msg)) {
      msg +=
        " — this database is missing migration 20260804091500_contract_internal_note_to_admin_notes. Run `supabase db push` against it.";
    } else if (/column/i.test(msg) && /does not exist/i.test(msg)) {
      msg +=
        " — this database is missing a migration. Run `supabase db push` against it.";
    }
    // RLS refusing the write is a PERMISSION answer, not a missing migration:
    // contract internal notes are super-admin-only at the database level.
    if (/row-level security/i.test(msg)) {
      msg += " — internal contract notes can only be written by a Super Admin.";
    }
    return msg;
  }

  /**
   * Run a lifecycle mutation. On success shows a toast that (optionally)
   * carries an Undo action reverting the change. Invalidation is decoupled so
   * it can never turn a successful write into a visible error.
   */
  async function runAction(
    fn: () => Promise<void>,
    successMsg: string,
    undo?: () => Promise<void>,
    opts?: { rethrow?: boolean; undoAnchor?: "movein" | "moveout" }
  ): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setActionErr(null);
    let ok = false;
    let failure: string | null = null;
    try {
      await fn();
      ok = true;
    } catch (e) {
      const msg = actionError(e);
      failure = msg;
      setActionErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
    if (!ok) {
      // Forms that hold typed text (the contract card's verification note and
      // internal note) must know the write failed, or they clear an input whose
      // content never reached the database — which reads as "saved".
      if (opts?.rethrow)
        throw new Error(
          failure ??
            t(
              "admin.tenantProfile.toastChangeNotSaved",
              "The change was not saved."
            )
        );
      return false;
    }
    if (successMsg) {
      // Toasts render behind the full-screen dialog overlay, so also surface a
      // persistent Undo affordance inside the dialog (see the header bar).
      if (undo) {
        setLastUndo({ label: successMsg, fn: undo, anchor: opts?.undoAnchor });
        toast.success(successMsg, {
          action: { label: "Undo", onClick: () => void runUndo(undo) },
        });
      } else {
        setLastUndo(null);
        toast.success(successMsg);
      }
    }
    refreshAfterWrite();
    return true;
  }

  async function runUndo(undo: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await undo();
      setLastUndo(null);
      toast.success(t("admin.tenantProfile.toastReverted", "Reverted."));
    } catch (e) {
      const msg = actionError(e);
      setActionErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
      refreshAfterWrite();
    }
  }

  function patchBooking(patch: Record<string, any>) {
    setOptimistic((o) => ({ ...o, ...patch }));
  }

  /**
   * The single funnel for every lifecycle write on the booking row — and
   * therefore the one place that can guarantee every action reaches the
   * Activity Timeline.
   *
   * The timeline used to show only the journey STAGES, which are derived from
   * the booking's timestamp columns. That reflects where the tenant stands, not
   * what anyone did: an action that reversed a stage (any Undo, marking a
   * deposit unpaid) made its stage event silently disappear, and an action
   * touching a column no stage reads left no trace at all. Auditing here means
   * the log records the action itself, reversals included.
   *
   * Best-effort by construction: logAudit swallows its own errors, so a failed
   * audit write can never fail the action the admin actually took.
   */
  async function updateBookingRow(
    patch: Record<string, any>,
    action: string = "booking_updated"
  ) {
    if (!bookingId)
      throw new Error(t("admin.tenantProfile.toastNoBooking", "No booking."));
    // Captured before the write, from the same merged row the UI is showing.
    const before = booking as Record<string, any>;
    const oldValues = Object.fromEntries(
      Object.keys(patch).map((k) => [k, before?.[k] ?? null])
    );
    const { error } = await supabase
      .from("bookings")
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq("id", bookingId);
    if (error) throw error;
    patchBooking(patch);
    await logAudit({
      action,
      resourceType: "booking",
      resourceId: String(bookingId),
      oldValues,
      newValues: patch,
    });
  }

  const markContractSigned = () => {
    const prev = {
      contract_status: booking.contract_status ?? null,
      contract_signed_at: booking.contract_signed_at ?? null,
    };
    return runAction(
      () =>
        updateBookingRow(
          {
            contract_status: "signed",
            contract_signed_at: new Date().toISOString(),
          },
          "contract_marked_signed"
        ),
      t(
        "admin.tenantProfile.toastContractMarkedSigned",
        "Contract marked signed"
      ),
      () => updateBookingRow(prev, "contract_marked_signed_undone")
    );
  };

  /** Contract generation needs both a booking and the host-supplied callback. */
  const canGenerateContract = !!onGenerateContract && !!bookingId;
  /**
   * Releasing the draft is a SEPARATE capability, wired through a separate
   * callback. The send button used to be disabled on `canGenerateContract` and
   * borrow its tooltip, so on a surface that wired one and not the other the UI
   * spoke about the wrong action — and "Contract generation isn't available on
   * this screen" is not an answer to why Send is greyed out.
   */
  const canSendContract = !!onSendContract && !!bookingId;

  /**
   * Build the PDF. `"draft"` (what the UI uses) emails nobody — the contract
   * parks at `contract_status = 'draft'` for review. `"send"` is the legacy
   * one-step path, only reachable with the hidden legacy button.
   */
  const generateContract = (
    templateId: string | null = null,
    mode: "draft" | "send" = "draft"
  ) =>
    runAction(
      async () => {
        if (!onGenerateContract || !bookingId) {
          throw new Error(
            t(
              "admin.tenantProfile.toastContractGenUnavailable",
              "Contract generation isn't available here."
            )
          );
        }
        await onGenerateContract(bookingId, {
          templateId,
          draft: mode === "draft",
        });
      },
      mode === "draft"
        ? t(
            "admin.tenantProfile.toastDraftGenerated",
            "Draft generated — nothing sent to the tenant yet"
          )
        : t(
            "admin.tenantProfile.toastContractSent",
            "Contract generated & sent for signature"
          )
    );

  /**
   * Record that an admin read the draft and what they checked. This is the
   * gate the send button waits on; regenerating the PDF clears it again
   * (create-contract nulls the trio server-side).
   *
   * Written CONDITIONALLY on the PDF this admin was actually looking at. Two
   * admins on the same tenant used to race: B's in-flight verify would land on
   * the PDF A had just regenerated in the meantime, stamping a document B never
   * read — the one outcome the gate exists to prevent. Matching
   * `contract_pdf_updated_at` means a verification can only ever attach to the
   * draft that was on screen; if it moved, B is told to re-read it.
   *
   * An actor is required: a verification with `contract_draft_verified_by: null`
   * is an accountability record with nobody in it.
   */
  const verifyContractDraft = async (note: string): Promise<void> => {
    await runAction(
      async () => {
        if (!bookingId) throw new Error("No booking.");
        if (!currentUserId) {
          throw new Error(
            t(
              "admin.tenantProfile.toastVerifyUnknownUser",
              "We could not tell who you are signed in as. Reload the page and verify again."
            )
          );
        }
        const seenPdfAt = booking.contract_pdf_updated_at ?? null;
        const patch = {
          contract_draft_verified_at: new Date().toISOString(),
          contract_draft_verified_by: currentUserId,
          contract_draft_verification_note: note,
        };
        let query = supabase
          .from("bookings")
          .update({ ...patch, updated_at: new Date().toISOString() })
          .eq("id", bookingId);
        // `is`/`eq` per null-ness — PostgREST needs `is.null` for a null match.
        query = seenPdfAt
          ? query.eq("contract_pdf_updated_at", seenPdfAt)
          : query.is("contract_pdf_updated_at", null);
        const { data, error } = await query.select("id");
        if (error) throw error;
        if (!data || data.length === 0) {
          // Zero rows has two causes, and they need different answers: the PDF
          // moved under us, or RLS refused the write (which returns no rows
          // rather than an error). Read the row back and say which.
          const { data: current } = await supabase
            .from("bookings")
            .select("contract_pdf_updated_at")
            .eq("id", bookingId)
            .maybeSingle();
          const nowPdfAt =
            (current as { contract_pdf_updated_at?: string | null } | null)
              ?.contract_pdf_updated_at ?? null;
          throw new Error(
            nowPdfAt !== seenPdfAt
              ? t(
                  "admin.tenantProfile.toastDraftStale",
                  "This draft was regenerated while you were reviewing it. Reopen the new draft, read it, then verify."
                )
              : t(
                  "admin.tenantProfile.toastVerifyRefused",
                  "The verification was refused. Contract verification is restricted to coliviq staff accounts."
                )
          );
        }
        patchBooking(patch);
      },
      t(
        "admin.tenantProfile.toastDraftVerified",
        "Draft verified — you can send it now"
      ),
      undefined,
      { rethrow: true }
    );
  };

  /**
   * Admin-only note about the contract. Stored as an `admin_notes` row
   * (kind = 'contract_internal'): admins read, super admins write, and RLS —
   * not the UI — is what enforces that. Kept separate from the verification
   * note: this is about the deal, so it survives PDF regeneration.
   */
  const saveContractInternalNote = async (note: string): Promise<void> => {
    await runAction(
      async () => {
        if (!bookingId) throw new Error("No booking.");
        await saveInternalNote.mutateAsync({
          bookingId,
          userId,
          body: note,
          createdBy: currentUserId ?? null,
        });
      },
      note.length > 0
        ? t("admin.tenantProfile.toastInternalNoteSaved", "Internal note saved")
        : t(
            "admin.tenantProfile.toastInternalNoteCleared",
            "Internal note cleared"
          ),
      undefined,
      { rethrow: true }
    );
  };

  /** Release the verified draft: status flip + the tenant's email. */
  const sendContractToTenant = () =>
    runAction(
      async () => {
        if (!onSendContract || !bookingId) {
          throw new Error(
            t(
              "admin.tenantProfile.toastSendUnavailable",
              "Sending isn't available here."
            )
          );
        }
        await onSendContract(bookingId);
        patchBooking({ contract_status: "ready_to_sign" });
      },
      t(
        "admin.tenantProfile.toastContractSentToTenant",
        "Contract sent to the tenant"
      )
    );

  const markDepositPaid = () => {
    // Booking status before the action, so undo can restore it in every path —
    // the deposit-paid DB trigger (and the flag path below) confirm the booking,
    // and the plain "reset to pending" undo used to leave it confirmed.
    const prevBookingStatus = booking.status ?? null;
    // Every write carries the ADMIN ATTESTATION (`marked_paid_by` /
    // `marked_paid_at`). Without it the row is a bare `status = 'paid'` with
    // nothing behind it, which `isPaymentEffectivelyPaid` deliberately ignores —
    // so the button used to "work" and then silently un-work on reopen, with the
    // tenant snapping back to Deposit pending. The attestation is what makes an
    // off-platform (cash / bank transfer, no uploadable proof) settlement
    // evidence: it records who settled it and when.
    const attest = () => ({
      status: "paid",
      paid_at: new Date().toISOString(),
      marked_paid_at: new Date().toISOString(),
      marked_paid_by: currentUserId ?? null,
    });
    // Which branch actually ran, captured at write time so undo doesn't guess
    // from a stale `payments` snapshot (the insert path had no row to find, so
    // the old undo silently fell through and did nothing).
    let done:
      | {
          kind: "row";
          id: string;
          prevStatus: string;
          prevPaidAt: string | null;
          /** Set only when this action topped the row up (see `topUpCents`). */
          prevAmountCents: number | null;
        }
      | { kind: "insert"; id: string }
      | {
          kind: "attest";
          ids: string[];
          prevAmountCents: Record<string, number>;
        }
      | null = null;
    // The row amount this action raised, so the post-write shortfall check reads
    // what we just wrote instead of the stale `payments` snapshot.
    let toppedUp: { id: string; cents: number } | null = null;
    // Raise a single live row that sits below the contractual deposit, so this
    // action actually settles the deposit instead of attesting a €0 row the
    // journey gate will keep ignoring. See depositTopUpCents for the guards.
    const topUpCents = (_row: any, live: any[]): number | null =>
      depositTopUpCents({
        liveDepositRows: live,
        expectedCents: depositState.expectedCents,
      });
    // Whether the deposit is actually SETTLED afterwards — a single row that
    // covers less than the contractual deposit leaves it partial, and the stage
    // legitimately stays at Deposit pending. Say so instead of letting the
    // tenant look promoted and then not be.
    let shortfallCents = 0;
    return runAction(
      async () => {
        if (!bookingId) throw new Error("No booking.");
        // Canceled / failed rows are voided attempts (same rule as
        // resolveDepositState) — never revive one by marking it paid.
        const liveDeposits = payments.filter(
          (p: any) =>
            p.type === "deposit" &&
            !["canceled", "failed"].includes(
              String(p.status ?? "").toLowerCase()
            )
        );
        const depositRow = liveDeposits.find((p: any) => p.status !== "paid");
        if (depositRow) {
          const topUp = topUpCents(depositRow, liveDeposits);
          const { error } = await supabase
            .from("payments")
            .update({
              ...attest(),
              ...(topUp != null ? { amount_cents: topUp } : {}),
            })
            .eq("id", depositRow.id);
          if (error) throw error;
          if (topUp != null)
            toppedUp = { id: String(depositRow.id), cents: topUp };
          done = {
            kind: "row",
            id: String(depositRow.id),
            prevStatus: depositRow.status ?? "pending",
            prevPaidAt: depositRow.paid_at ?? null,
            prevAmountCents:
              topUp != null ? Number(depositRow.amount_cents) || 0 : null,
          };
          // Owner "Deposit received" mail — this button settles the row with a
          // direct UPDATE, same as editPayment above, so it needs the same
          // notify-owner-lifecycle hop (src/lib/owner-payment-notify.ts).
          // `depositRow.amount_cents` is the pre-write snapshot — when this
          // action topped the row up (the €0 case), report what was just
          // written instead of the stale amount.
          void reportOwnerPaymentNotification({
            id: String(depositRow.id),
            type: "deposit",
            booking_id: bookingId,
            amount_cents: topUp ?? (depositRow as any).amount_cents ?? null,
            due_date: (depositRow as any).due_date ?? null,
          });
        } else if (liveDeposits.length === 0) {
          // No deposit payment row exists — create one (paid). The deposit-paid
          // DB trigger then confirms the booking. The row also gives us a
          // payment to attach the bank-transfer proof to. Refuse to mint a €0
          // paid row (a null deposit_amount would otherwise satisfy the
          // deposit-paid predicate everywhere for free).
          const cents = Math.round((Number(booking.deposit_amount) || 0) * 100);
          if (cents <= 0) {
            throw new Error(
              t(
                "admin.tenantProfile.toastSetDepositFirst",
                "Set a deposit amount on the booking before marking the deposit paid."
              )
            );
          }
          const { data, error } = await supabase
            .from("payments")
            .insert(
              operatorStamped<"payments">({
                booking_id: bookingId,
                // The tenant the row belongs to. Without it the row is invisible
                // to them: the Dashboard reads payments with
                // `.eq("user_id", user.id)` and the "Users can view their
                // payments" RLS policy is `auth.uid() = user_id`, so an
                // admin-created deposit with a NULL user_id renders as "No
                // deposit payment recorded yet" on the tenant's own screen while
                // this dialog shows it Paid — no paid-rule change can reach a row
                // the tenant never receives (#1328 follow-up).
                user_id: (booking as any).user_id ?? userId,
                type: "deposit",
                amount_cents: cents,
                currency: "EUR",
                due_date: booking.move_in_date || booking.start_date || null,
                // Persisted on the payment row — keep language-invariant.
                description: "Security deposit",
                ...attest(),
              })
            )
            .select("id")
            .single();
          if (error) throw error;
          done = { kind: "insert", id: String(data.id) };
          void reportOwnerPaymentNotification({
            id: String(data.id),
            type: "deposit",
            booking_id: bookingId,
            amount_cents: cents,
            due_date: booking.move_in_date || booking.start_date || null,
          });
        } else {
          // Rows exist and all already say "paid" — but unattested and with no
          // proof, so the deposit gate ignores them (legacy rows, an import, or
          // a status flipped by another surface). Attest them: that is exactly
          // what this button is asserting, and it's the only branch that can
          // make an already-"paid" deposit actually count.
          const ids = liveDeposits.map((p: any) => String(p.id));
          const { error } = await supabase
            .from("payments")
            .update({
              marked_paid_at: new Date().toISOString(),
              marked_paid_by: currentUserId ?? null,
            })
            .in("id", ids);
          if (error) throw error;
          // An already-"paid" row can still be short of the contractual deposit
          // (the €0 proof-upload row is the common case), and attesting it alone
          // leaves the gate shut with nothing left to click. Top it up on the
          // same terms as the unpaid branch.
          const prevAmountCents: Record<string, number> = {};
          const short = topUpCents(liveDeposits[0], liveDeposits);
          if (short != null) {
            const row = liveDeposits[0];
            const { error: aErr } = await supabase
              .from("payments")
              .update({ amount_cents: short })
              .eq("id", row.id);
            if (aErr) throw aErr;
            prevAmountCents[String(row.id)] = Number(row.amount_cents) || 0;
            toppedUp = { id: String(row.id), cents: short };
          }
          const { error: bErr } = await supabase
            .from("bookings")
            .update({ manually_marked_paid: true, status: "confirmed" })
            .eq("id", bookingId);
          if (bErr) throw bErr;
          patchBooking({ manually_marked_paid: true, status: "confirmed" });
          done = { kind: "attest", ids, prevAmountCents };
          // These rows already said "paid" before this button ran — exactly
          // the "settled by a surface that never notified" shape
          // recordManualRentPaidInLedger self-heals for. Fire the mail here
          // too; the booking-scoped stamp makes a repeat call a no-op if it
          // already went out.
          const attestedRows = liveDeposits.filter((p: any) =>
            ids.includes(String(p.id))
          );
          void reportOwnerPaymentNotification({
            id: ids[0],
            type: "deposit",
            booking_id: bookingId,
            // A just-topped-up row's amount_cents in `liveDeposits` is still the
            // pre-write value (the €0 case is the common one) — read what we
            // actually wrote, not the stale snapshot.
            amount_cents: attestedRows.reduce(
              (sum: number, p: any) =>
                sum +
                (toppedUp && String(p.id) === toppedUp.id
                  ? toppedUp.cents
                  : Number(p.amount_cents) || 0),
              0
            ),
            due_date: (attestedRows[0] as any)?.due_date ?? null,
          });
        }
        // Post-write shortfall check against the CONTRACTUAL deposit. The insert
        // path mints a row at exactly the expected amount, so only the two
        // in-place paths can leave a genuine installment shortfall. Count a row
        // as settled only if this action attested it or it already counted —
        // summing every live row would silently include an untouched pending
        // installment and report a settled deposit that isn't one.
        if (done.kind !== "insert") {
          const attestedIds = new Set(
            done.kind === "row" ? [done.id] : done.ids
          );
          const settledCents = payments
            .filter((p: any) => p.type === "deposit")
            .filter(
              (p: any) =>
                attestedIds.has(String(p.id)) ||
                isPaymentEffectivelyPaid(
                  p as any,
                  (id) => proofByPayment[id] ?? null
                )
            )
            .reduce(
              (sum: number, p: any) =>
                sum +
                (toppedUp && String(p.id) === toppedUp.id
                  ? toppedUp.cents
                  : Number(p.amount_cents) || 0),
              0
            );
          shortfallCents = Math.max(
            0,
            depositState.expectedCents - settledCents
          );
        }
        // Only claim "paid" optimistically when the write actually settles the
        // full deposit — otherwise the override would contradict the predicate
        // and raise the very mismatch banner it's meant to avoid.
        if (shortfallCents === 0) setDepositPaidOverride(true);
        // Writes straight to payments/bookings rather than through
        // updateBookingRow, so it audits itself. The stage event covers the
        // forward step; the log is what survives the undo below.
        await logAudit({
          action: "deposit_marked_paid",
          resourceType: "booking",
          resourceId: String(bookingId),
          oldValues: null,
          newValues: {
            manually_marked_paid: true,
            shortfall_cents: shortfallCents,
          },
        });
      },
      t("admin.tenantProfile.toastDepositPaid", "Deposit marked paid"),
      async () => {
        if (!done) return;
        if (done.kind === "row") {
          await supabase
            .from("payments")
            .update({
              status: done.prevStatus,
              paid_at: done.prevPaidAt,
              marked_paid_at: null,
              marked_paid_by: null,
              // Only when this action raised it — an untouched amount is not
              // ours to rewrite.
              ...(done.prevAmountCents != null
                ? { amount_cents: done.prevAmountCents }
                : {}),
            })
            .eq("id", done.id);
        } else if (done.kind === "insert") {
          // Delete the row we created rather than resetting a non-existent prior.
          await supabase.from("payments").delete().eq("id", done.id);
        } else {
          // Withdraw the attestation only — the rows' own "paid" status predates
          // this action and isn't ours to reset.
          await supabase
            .from("payments")
            .update({ marked_paid_at: null, marked_paid_by: null })
            .in("id", done.ids);
          // …but a top-up IS ours, so put the original amount back.
          for (const [id, cents] of Object.entries(done.prevAmountCents)) {
            await supabase
              .from("payments")
              .update({ amount_cents: cents })
              .eq("id", id);
          }
        }
        // Restore the booking: clear the manual flag, and put status back to
        // what it was (the trigger / flag path may have flipped it to confirmed).
        const revert: Record<string, any> = { manually_marked_paid: false };
        if (prevBookingStatus != null) revert.status = prevBookingStatus;
        if (bookingId)
          await supabase.from("bookings").update(revert).eq("id", bookingId);
        patchBooking(revert);
        setDepositPaidOverride(false);
        await logAudit({
          action: "deposit_marked_paid_undone",
          resourceType: "booking",
          resourceId: String(bookingId),
          oldValues: { manually_marked_paid: true },
          newValues: { manually_marked_paid: false },
        });
      }
    ).then((ok) => {
      if (ok && shortfallCents > 0) {
        toast.info(
          `Recorded — but €${(shortfallCents / 100).toFixed(2)} of the contractual deposit is still outstanding, so the journey stays at Deposit pending.`
        );
      }
      return ok;
    });
  };

  const markKeyPickedUp = (pickedUpAt?: string) => {
    // COL-409: this same action also fires when a manager corrects an
    // already-recorded pickup, not just the first mark — Undo must restore
    // the prior timestamp then, not wipe a real pickup back to null.
    const prevPickedUpAt = (booking as any).key_picked_up_at ?? null;
    return runAction(
      async () => {
        await updateBookingRow(
          { key_picked_up_at: pickedUpAt || new Date().toISOString() },
          "key_marked_picked_up"
        );
        // Owner t("admin.tenantProfile.toastKeysHandedOver", "Keys handed over") mail — fire-and-forget, same pattern as
        // the tenant lifecycle email invoke below. The idempotency stamp
        // claimed inside the function means a retry never double-sends.
        void supabase.functions
          .invoke("notify-owner-lifecycle", {
            body: { event: "keys_handed_over", bookingId },
          })
          .catch(() => {});
      },
      t("admin.tenantProfile.toastKeyPickedUp", "Key marked as picked up"),
      () =>
        updateBookingRow(
          { key_picked_up_at: prevPickedUpAt },
          "key_marked_picked_up_undone"
        )
    );
  };

  const markKeyReturned = (returnedAt?: string) => {
    // COL-409: same reasoning as markKeyPickedUp — Undo must restore the
    // prior timestamp when correcting an already-recorded return.
    const prevReturnedAt = (booking as any).key_returned_at ?? null;
    return runAction(
      async () => {
        await updateBookingRow(
          { key_returned_at: returnedAt || new Date().toISOString() },
          "key_marked_returned"
        );
        void supabase.functions
          .invoke("notify-owner-lifecycle", {
            body: { event: "keys_returned", bookingId },
          })
          .catch(() => {});
      },
      t(
        "admin.tenantProfile.toastKeyReturned",
        "Key marked as returned — moved out"
      ),
      () =>
        updateBookingRow(
          { key_returned_at: prevReturnedAt },
          "key_marked_returned_undone"
        )
    );
  };

  /**
   * COL-425: file a move-in/move-out protocol for a tenant who cannot
   * complete the mobile flow — under the admin's name, no signature. Finds
   * (or creates) the draft protocol, uploads any evidence, marks it
   * submitted, files it into the tenant's documents, and — for move-in only
   * — advances the booking the same way the old markMoveInComplete() did
   * (move-out's "moveout_protocol_pending" stage is left by the protocol
   * submission itself; there is no separate move-out completion column).
   */
  const submitProtocolOnBehalf = async (
    kind: "move_in" | "move_out",
    { note, files }: { note: string; files: File[] }
  ): Promise<boolean> => {
    const existingRaw = kind === "move_in" ? moveInProtocol : moveOutProtocol;
    // Never reuse a tenant's own in-progress draft — it may hold a walkthrough
    // they are part-way through, and filing under an admin's name would both
    // destroy that work and permanently hide it once DoneStep starts treating
    // the row as operator-recorded. Only repair a `submitted` row with no PDF
    // (the artifact a force-staged row can leave behind) — same restriction
    // `useRecordProtocolOnBehalf` already applies to the other admin-filing path.
    const existing =
      existingRaw?.status === "submitted" && !existingRaw.pdf_storage_path
        ? existingRaw
        : null;
    const prevProtocolStatus = existing?.status ?? null;
    const prevSubmittedAt = existing?.submitted_at ?? null;
    const prevConfirmed = (booking as any).move_in_confirmed_at ?? null;
    const prevBookingStatus = booking.status ?? null;
    let protocolId: string = existing?.id ?? "";
    let createdProtocol = !protocolId;
    let ref = existing?.ref ?? "";
    let tenantDocumentId: string | null = null;

    return runAction(
      async () => {
        if (!bookingId) throw new Error("No booking.");
        if (!protocolId) {
          ref = generateProtocolRef();
          const { data, error } = await supabase
            .from("handover_protocols")
            .insert({
              ref,
              booking_id: bookingId,
              user_id: booking.user_id ?? null,
              kind,
              status: "draft",
            })
            .select("id")
            .single();
          if (error) throw error;
          protocolId = data.id as string;
          createdProtocol = true;
        }

        for (const file of files) {
          const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, "_");
          const path = `${protocolId}/admin-attachments/${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}-${safeName}`;
          const { error: upErr } = await supabase.storage
            .from(PROTOCOL_STORAGE_BUCKET)
            .upload(path, file, {
              upsert: false,
              contentType: file.type || "application/octet-stream",
            });
          if (upErr) throw upErr;
          const { error: attErr } = await supabase
            .from("handover_protocol_attachments")
            .insert({
              protocol_id: protocolId,
              storage_path: path,
              file_name: file.name,
              file_size: file.size,
              mime_type: file.type || null,
              uploaded_by: currentUserId ?? null,
            });
          if (attErr) throw attErr;
        }

        const now = new Date().toISOString();
        const recordedByName =
          (currentUser?.user_metadata?.name as string | undefined)?.trim() ||
          currentUser?.email ||
          // Persisted in recorded_by_name and embedded in the generated PDF —
          // keep language-invariant, same as the payment description above.
          "coliviq operator";

        // Same document every other admin-recorded protocol produces
        // (attestation page + note + every attached file merged in) — without
        // this, the tenant/owner-facing "PDF" is a near-empty tenant-walkthrough
        // template with none of what was actually filed, since this protocol
        // has no photos/inventory/defect rows for that renderer to draw from.
        const pdfBlob = await buildRecordedProtocolPdf({
          ref,
          kind,
          walkthroughDate: now,
          recordedAt: now,
          recordedByName,
          note,
          booking: {
            property_name: propertyTitle === "—" ? null : propertyTitle,
            room_number: roomTitle === "—" ? null : roomTitle,
            address: address || null,
          },
          tenant: { name: name || null, email: email || null },
          files,
        });
        const pdfPath = `pdfs/${protocolId}.pdf`;
        const { error: pdfUpErr } = await supabase.storage
          .from(PROTOCOL_STORAGE_BUCKET)
          .upload(pdfPath, pdfBlob, {
            upsert: true,
            contentType: "application/pdf",
          });
        if (pdfUpErr) throw pdfUpErr;

        const baseProtoUpdate = {
          status: "submitted",
          submitted_at: now,
          pdf_storage_path: pdfPath,
          admin_submitted_by: currentUserId ?? null,
          admin_submitted_note: note || null,
        };
        const attributionUpdate = {
          // Same attribution columns the other admin-recorded path writes,
          // so isAdminRecorded() and every reader built around it (the
          // tenant dashboard card, DoneStep, ProtocolDetailPage) treat this
          // protocol the same way regardless of which dialog filed it.
          recorded_by_user_id: currentUserId ?? null,
          recorded_by_name: recordedByName,
          recorded_at: now,
          // The profile card's protocol note ("adminNote") reads this column,
          // not admin_submitted_note — set both so the note surfaces there too.
          admin_note: note || null,
        };
        const { error: protoErr } = await supabase
          .from("handover_protocols")
          .update({ ...baseProtoUpdate, ...attributionUpdate })
          .eq("id", protocolId);
        if (protoErr) {
          // A database behind on migration 20260818100000 still needs this
          // action to succeed — retry without the attribution columns, same
          // fallback useRecordProtocolOnBehalf already applies for the other
          // admin-filing path.
          if (!isMissingColumn(protoErr)) throw protoErr;
          const { error: retryErr } = await supabase
            .from("handover_protocols")
            .update(baseProtoUpdate)
            .eq("id", protocolId);
          if (retryErr) throw retryErr;
        }

        // File the merged PDF into the tenant's documents, same as the
        // tenant-walkthrough finalizer does for a self-submitted protocol —
        // there is always a real, viewable PDF now, unlike before this fix
        // where a note-only submission had nothing to file.
        {
          const { data: docData, error: docErr } = await supabase
            .from("tenant_documents")
            .insert(
              operatorStamped<"tenant_documents">({
                booking_id: bookingId,
                user_id: booking.user_id ?? null,
                document_type:
                  kind === "move_in" ? "move_in_protocol" : "move_out_protocol",
                // Persisted in tenant_documents.title — keep language-invariant,
                // same as recordedByName and the payment description above.
                title:
                  kind === "move_in" ? "Move-in protocol" : "Move-out protocol",
                // Bucket-qualified so TenantDocumentsSection's resolver signs
                // it against `handover-protocols`.
                file_url: `${PROTOCOL_STORAGE_BUCKET}/${pdfPath}`,
                file_name: `${ref}.pdf`,
                status: "approved",
                source: "admin",
                notes: note || null,
              })
            )
            .select("id")
            .single();
          if (docErr) throw docErr;
          tenantDocumentId = docData.id as string;
        }

        await logAudit({
          action:
            kind === "move_in"
              ? "move_in_protocol_submitted_by_admin"
              : "move_out_protocol_submitted_by_admin",
          resourceType: "handover_protocol",
          resourceId: protocolId,
          newValues: { status: "submitted", note: note || null },
        });

        if (kind === "move_in") {
          await updateBookingRow(
            {
              status: "active",
              move_in_confirmed_at: prevConfirmed ?? now,
            },
            "move_in_confirmed"
          );
        }
      },
      `${kind === "move_in" ? "Move-in" : "Move-out"} protocol submitted on the tenant's behalf`,
      // Undo: the uploaded evidence stays in storage (it did happen), but the
      // decision it backed — the protocol's submitted state, the filed
      // document, and (move-in) the booking advancing to Active — reverts.
      async () => {
        if (tenantDocumentId) {
          await supabase
            .from("tenant_documents")
            .delete()
            .eq("id", tenantDocumentId);
        }
        if (createdProtocol) {
          await supabase
            .from("handover_protocols")
            .delete()
            .eq("id", protocolId);
        } else {
          await supabase
            .from("handover_protocols")
            .update({
              status: prevProtocolStatus,
              submitted_at: prevSubmittedAt,
              pdf_storage_path: null,
              admin_submitted_by: null,
              admin_submitted_note: null,
              recorded_by_user_id: null,
              recorded_by_name: null,
              recorded_at: null,
              admin_note: null,
            })
            .eq("id", protocolId);
        }
        if (kind === "move_in") {
          await updateBookingRow(
            {
              status: prevBookingStatus,
              move_in_confirmed_at: prevConfirmed,
            },
            "move_in_confirmed_undone"
          );
        }
      }
    );
  };

  /**
   * File a handover protocol on the tenant's behalf.
   *
   * No Undo, deliberately — and this is the one lifecycle action on this
   * surface without one. Every other undo here flips a column back; this one
   * would have to delete an uploaded PDF, un-submit a row and re-block a
   * deposit bundle that may already have been assembled and emailed. A
   * half-completed unwind is worse than none, so the guard is the dialog's
   * confirm step, and the reversal is the super-admin stage override.
   */
  const runRecordProtocol = async (
    kind: "move_in" | "move_out",
    payload: RecordProtocolSubmit,
    /** Set when swapping the document on an operator-recorded protocol. */
    replaceProtocol?: { id: string; before: Record<string, Json | undefined> }
  ) => {
    if (!bookingId) return;
    const ok = await runAction(
      async () => {
        const result = await recordProtocol
          .mutateAsync({
            bookingId,
            tenantUserId: userId ?? (booking as any)?.user_id ?? null,
            kind,
            files: payload.files,
            walkthroughDate: payload.walkthroughDate,
            note: payload.note,
            booking: {
              property_name: propertyTitle === "—" ? null : propertyTitle,
              room_number: roomTitle === "—" ? null : roomTitle,
              address: address || null,
            },
            tenant: { name: name || null, email: email || null },
            notifyTenant: payload.notifyTenant,
            replaceProtocolId: replaceProtocol?.id,
          })
          .catch(async (err: unknown) => {
            // The hook has no `t`; it says why a replacement was refused and
            // the message is translated here, where the admin reads it.
            if (!(err instanceof ReplaceProtocolError)) throw err;
            // A half-done replacement still changed the PDF, and it cannot be
            // put back — so it gets its own trail even though the action failed.
            // (logAudit swallows its own errors.)
            if (err.code === "partial" && replaceProtocol) {
              await logAudit({
                action:
                  kind === "move_in"
                    ? "move_in_protocol_replaced"
                    : "move_out_protocol_replaced",
                resourceType: "protocol",
                resourceId: replaceProtocol.id,
                oldValues: replaceProtocol.before,
                newValues: {
                  partial: true,
                  note: payload.note,
                  files: payload.files.map((f) => f.name),
                },
              });
            }
            throw new Error(
              err.code === "not_found"
                ? t(
                    "admin.tenantProfile.replaceRefusedNotFound",
                    "This protocol could not be found for the tenant. Reload and try again."
                  )
                : err.code === "no_document"
                  ? t(
                      "admin.tenantProfile.replaceRefusedNoDocument",
                      "There is no filed document to replace. Use Record protocol instead."
                    )
                  : err.code === "partial"
                    ? t(
                        "admin.tenantProfile.replaceFailedPartial",
                        "The new document was saved, but the protocol record could not be updated. Replace it again to finish."
                      )
                    : err.code === "bundle_generated"
                      ? t(
                          "admin.tenantProfile.replaceRefusedBundleGenerated",
                          "A deposit settlement bundle has already been generated for this booking. Protocols can no longer be added or replaced."
                        )
                      : t(
                          "admin.tenantProfile.replaceRefusedNotRecorded",
                          "Only a protocol an operator recorded can be replaced. A tenant's own submission stays as filed."
                        )
            );
          });
        await logAudit({
          action: replaceProtocol
            ? kind === "move_in"
              ? "move_in_protocol_replaced"
              : "move_out_protocol_replaced"
            : kind === "move_in"
              ? "move_in_protocol_recorded"
              : "move_out_protocol_recorded",
          resourceType: "protocol",
          resourceId: result.protocolId,
          // For a replacement, what the overwritten record said — the PDF itself
          // is gone, so this is the trail of what it was and who filed it.
          oldValues: replaceProtocol?.before ?? null,
          newValues: {
            ref: result.ref,
            walkthrough_date: payload.walkthroughDate,
            files: payload.files.map((f) => f.name),
            note: payload.note,
            tenant_emailed: payload.notifyTenant,
          },
        });
        if (!result.attributionStored) {
          toast.warning(
            "Recorded, but this database is missing migration 20260818100000 — the card cannot show who filed it. The PDF still states it.",
            { duration: 8000 }
          );
        }
        if (!result.documentFiled) {
          // The protocol is recorded and the bundle unblocked, but the PDF did
          // not reach tenant_documents — so it is missing from the Documents
          // tab, the owner portal and the tenant's own documents. Say so:
          // silence here reads as "filed everywhere" to whoever looks next.
          //
          // Re-recording cannot fix this: useRecordProtocolOnBehalf refuses
          // any row that is already `submitted` with a PDF, which this row
          // now is. The action below re-attempts only the filing step.
          const protocolId = result.protocolId;
          toast.warning(
            "Recorded, but the PDF could not be filed into the document lists — it will not appear in Documents, the owner portal or the tenant's documents.",
            {
              duration: 10000,
              action: {
                label: t(
                  "admin.tenantProfile.actionRetryFiling",
                  "Retry filing"
                ),
                onClick: () => {
                  refileProtocolDocument.mutate(protocolId, {
                    onSuccess: () =>
                      toast.success(
                        t(
                          "admin.tenantProfile.toastProtocolFiled",
                          "Filed into the document lists."
                        )
                      ),
                    onError: () =>
                      toast.error(
                        t(
                          "admin.tenantProfile.toastProtocolFileRetryFailed",
                          "Still couldn't file it. Try again in a moment or contact engineering."
                        )
                      ),
                  });
                },
              },
            }
          );
        }
      },
      replaceProtocol
        ? kind === "move_in"
          ? t(
              "admin.tenantProfile.toastMoveInReplaced",
              "Move-in protocol replaced"
            )
          : t(
              "admin.tenantProfile.toastMoveOutReplaced",
              "Move-out protocol replaced"
            )
        : kind === "move_in"
          ? t(
              "admin.tenantProfile.toastMoveInRecorded",
              "Move-in protocol recorded"
            )
          : t(
              "admin.tenantProfile.toastMoveOutRecorded",
              "Move-out protocol recorded"
            )
    );
    if (ok) {
      setRecordProtocolKind(null);
      setReplaceProtocolKind(null);
    }
  };

  // COL-427: grant the tenant an indefinite reopen (no more 48h grace) after
  // their protocol window auto-accepted/auto-completed. Kind-scoped by
  // construction — each only ever touches its own `move_in_*`/`move_out_*`
  // columns, so reopening one never affects the other. Also clears any
  // earlier "manually closed" stamp — a fresh reopen supersedes it.
  const reopenMoveInProtocol = () => {
    const prevAutoAcceptedAt =
      (booking as any).move_in_auto_accepted_at ?? null;
    const prevReopenedAt =
      (booking as any).move_in_protocol_reopened_at ?? null;
    const prevManuallyClosedAt =
      (booking as any).move_in_protocol_manually_closed_at ?? null;
    const patch = reopenProtocolPatch("move_in");
    return runAction(
      () => updateBookingRow(patch, "move_in_protocol_reopened"),
      t(
        "admin.tenantProfile.toastMoveInReopened",
        "Move-in protocol reopened — the tenant can complete it again"
      ),
      () =>
        updateBookingRow(
          {
            move_in_auto_accepted_at: prevAutoAcceptedAt,
            move_in_protocol_reopened_at: prevReopenedAt,
            move_in_protocol_manually_closed_at: prevManuallyClosedAt,
          },
          "move_in_protocol_reopened_undone"
        )
    );
  };

  const reopenMoveOutProtocol = () => {
    const prevAutoCompletedAt =
      (booking as any).move_out_auto_completed_at ?? null;
    const prevReopenedAt =
      (booking as any).move_out_protocol_reopened_at ?? null;
    const prevManuallyClosedAt =
      (booking as any).move_out_protocol_manually_closed_at ?? null;
    const patch = reopenProtocolPatch("move_out");
    return runAction(
      () => updateBookingRow(patch, "move_out_protocol_reopened"),
      t(
        "admin.tenantProfile.toastMoveOutReopened",
        "Move-out protocol reopened — the tenant can complete it again"
      ),
      () =>
        updateBookingRow(
          {
            move_out_auto_completed_at: prevAutoCompletedAt,
            move_out_protocol_reopened_at: prevReopenedAt,
            move_out_protocol_manually_closed_at: prevManuallyClosedAt,
          },
          "move_out_protocol_reopened_undone"
        )
    );
  };

  // COL-427: re-lock a reopened protocol for the tenant. Deliberately a pure
  // toggle — clears the reopen stamp and, ONLY when nothing was submitted
  // while reopened, marks it "manually closed" (drives the tenant's neutral
  // "closed for you at this time" copy instead of the deemed-accepted one).
  // Never files a submission itself: PR #1220 did that (routing every close
  // through the tick's autoSubmitProtocol helper) and got reverted for it —
  // an admin closing the window must never look, from the tenant's side, like
  // they submitted something. If the tenant genuinely never submits,
  // protocol-reminder-tick's own next run is what deems it submitted, exactly
  // as if the reopen had never happened.
  const closeMoveInProtocol = () => {
    const prevReopenedAt =
      (booking as any).move_in_protocol_reopened_at ?? null;
    const prevManuallyClosedAt =
      (booking as any).move_in_protocol_manually_closed_at ?? null;
    // `isRealSubmission`, not the plain *Submitted flag: the tick's
    // deemed-accepted placeholder also carries status 'submitted', and
    // treating it as a submission meant closing a reopen granted over one
    // left no closed stamp — the tenant card then fell back to the
    // auto-accepted copy instead of the neutral "closed for you" one.
    const manuallyClosedAt = isRealSubmission(moveInProtocol)
      ? prevManuallyClosedAt
      : new Date().toISOString();
    return runAction(
      () =>
        updateBookingRow(
          {
            move_in_protocol_reopened_at: null,
            move_in_protocol_manually_closed_at: manuallyClosedAt,
          },
          "move_in_protocol_closed"
        ),
      t(
        "admin.tenantProfile.toastMoveInClosed",
        "Move-in protocol closed for the tenant"
      ),
      () =>
        updateBookingRow(
          {
            move_in_protocol_reopened_at: prevReopenedAt,
            move_in_protocol_manually_closed_at: prevManuallyClosedAt,
          },
          "move_in_protocol_closed_undone"
        )
    );
  };

  const closeMoveOutProtocol = () => {
    const prevReopenedAt =
      (booking as any).move_out_protocol_reopened_at ?? null;
    const prevManuallyClosedAt =
      (booking as any).move_out_protocol_manually_closed_at ?? null;
    // `isRealSubmission`, not the plain *Submitted flag: the tick's
    // deemed-accepted placeholder also carries status 'submitted', and
    // treating it as a submission meant closing a reopen granted over one
    // left no closed stamp — the tenant card then fell back to the
    // auto-accepted copy instead of the neutral "closed for you" one.
    const manuallyClosedAt = isRealSubmission(moveOutProtocol)
      ? prevManuallyClosedAt
      : new Date().toISOString();
    return runAction(
      () =>
        updateBookingRow(
          {
            move_out_protocol_reopened_at: null,
            move_out_protocol_manually_closed_at: manuallyClosedAt,
          },
          "move_out_protocol_closed"
        ),
      t(
        "admin.tenantProfile.toastMoveOutClosed",
        "Move-out protocol closed for the tenant"
      ),
      () =>
        updateBookingRow(
          {
            move_out_protocol_reopened_at: prevReopenedAt,
            move_out_protocol_manually_closed_at: prevManuallyClosedAt,
          },
          "move_out_protocol_closed_undone"
        )
    );
  };

  const initiateMoveOut = () => {
    const prev = booking.status ?? "active";
    const prevNotice = (booking as any).move_out_notice_at ?? null;
    // Same as move-in: t("admin.tenantProfile.toastMoveOutScheduled", "Move-out scheduled") is gated on `move_out_notice_at`,
    // so the notice timestamp is what actually advances the stage.
    return runAction(
      () =>
        updateBookingRow(
          {
            status: "notice_given",
            move_out_notice_at: prevNotice ?? new Date().toISOString(),
          },
          "move_out_initiated"
        ),
      t("admin.tenantProfile.toastMoveOutInitiated", "Move-out initiated"),
      () =>
        updateBookingRow(
          {
            status: prev,
            move_out_notice_at: prevNotice,
          },
          "move_out_initiated_undone"
        )
    );
  };

  const confirmMoveOutDate = (
    date: string,
    opts?: {
      successMsg?: string;
      undoAnchor?: "moveout";
      /** Run alongside the booking revert — e.g. flip a decided request row
       *  back to pending, so Undo actually undoes the whole decision. */
      onUndoExtra?: () => Promise<void>;
    }
  ) => {
    const prev = {
      status: booking.status ?? "notice_given",
      move_out_date: booking.move_out_date ?? null,
    };
    return runAction(
      async () => {
        await updateBookingRow(
          {
            status: "move_out_pending",
            move_out_date: date,
            // Confirming a date implies notice was given; without the notice
            // timestamp the earlier gate would block this stage.
            move_out_notice_at:
              (booking as any).move_out_notice_at ?? new Date().toISOString(),
          },
          "move_out_date_confirmed"
        );
        // COL-304: send the "Dein Auszug ist bestätigt" email immediately.
        // Fire-and-forget — the hourly tenant-lifecycle-email-tick is the
        // safety net if this invoke fails, and the send-log guarantees the
        // tenant never gets it twice.
        void supabase.functions
          .invoke("tenant-lifecycle-email-tick", {
            body: { bookingId },
          })
          .catch(() => {});
        // Owner t("admin.tenantProfile.toastMoveOutConfirmed", "Move-out date confirmed") mail — same fire-and-forget shape.
        void supabase.functions
          .invoke("notify-owner-lifecycle", {
            body: { event: "move_out_date_confirmed", bookingId },
          })
          .catch(() => {});
      },
      opts?.successMsg ?? "Move-out date confirmed",
      async () => {
        await updateBookingRow(prev, "move_out_date_confirmed_undone");
        await opts?.onUndoExtra?.();
      },
      opts?.undoAnchor ? { undoAnchor: opts.undoAnchor } : undefined
    );
  };

  /**
   * Checkout protocol (Neel Slack DM 2026-10-19): approve a tenant's
   * requested move-out date. Reuses `confirmMoveOutDate` verbatim — same
   * booking write, same tenant/owner emails — so "confirming starts the
   * move-out sequence with this date" is a single call, not new sequencing.
   * The success banner is anchored to the Move-out tab (green, with Undo),
   * per the approved mockup, rather than the dialog-level amber one.
   */
  async function approveMoveOutRequest(
    requestId: string,
    requestedDate: string
  ) {
    const refreshRequestCaches = () => {
      queryClient.invalidateQueries({
        queryKey: ["admin.move-out-request", bookingId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin.move-out-request.latest", bookingId],
      });
    };

    // Claim the request row BEFORE writing the booking: a stale Brain card
    // or a second admin tab racing to confirm the same request must not
    // both pass this gate and both overwrite the booking / resend the
    // lifecycle emails. Whoever's UPDATE actually flips a pending row to
    // approved is the only caller that goes on to touch the booking.
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const { data: claimedRows, error: claimError } = await supabase
      .from("move_out_date_requests")
      .update({
        status: "approved",
        decided_by: user?.id ?? null,
        decided_at: new Date().toISOString(),
      })
      .eq("id", requestId)
      .eq("status", "pending")
      .select("id");
    if (claimError) {
      toast.error(
        t(
          "admin.tenantTabs.moveOut.toastClaimFailed",
          "Couldn't mark this request as approved — nothing was confirmed."
        )
      );
      return;
    }
    if (!claimedRows?.length) {
      toast.warning(
        t(
          "admin.tenantTabs.moveOut.toastAlreadyDecided",
          "This request was already decided elsewhere."
        )
      );
      refreshRequestCaches();
      return;
    }

    // "{date}" is TODAY — the day this decision was made — not the
    // tenant's requested move-out date, which the facts box above already
    // shows.
    const ok = await confirmMoveOutDate(requestedDate, {
      successMsg: t(
        "admin.tenantTabs.moveOut.tenantRequestConfirmed",
        "Tenant request confirmed · {date} by you"
      ).replace(
        "{date}",
        formatDateNumeric(new Date().toISOString(), language)
      ),
      undoAnchor: "moveout",
      // Undo must undo the WHOLE decision, not just the booking write — a
      // request stuck on "approved" after Undo disappears from every
      // pending-only query (the notification card, the header badge) and
      // the admin loses the decision with no way back to it.
      onUndoExtra: async () => {
        const { error: undoErr } = await supabase
          .from("move_out_date_requests")
          .update({ status: "pending", decided_by: null, decided_at: null })
          .eq("id", requestId);
        if (undoErr) {
          toast.error(
            t(
              "admin.tenantTabs.moveOut.toastUndoRowFailed",
              "Move-out reverted, but the request is stuck as approved — reject it by hand."
            )
          );
        }
        refreshRequestCaches();
      },
    });
    if (!ok) {
      // The row is already claimed but the booking write never actually
      // landed (runAction already showed its own error toast) — put it
      // back to pending rather than leave it reading "approved" with
      // nothing confirmed.
      const { error: revertErr } = await supabase
        .from("move_out_date_requests")
        .update({ status: "pending", decided_by: null, decided_at: null })
        .eq("id", requestId)
        .eq("status", "approved");
      if (revertErr) {
        toast.error(
          t(
            "admin.tenantTabs.moveOut.toastClaimRevertFailed",
            "Move-out wasn't confirmed, and the request is stuck as approved — fix it by hand."
          )
        );
      }
      refreshRequestCaches();
      return;
    }
    refreshRequestCaches();
  }

  /**
   * Super-admin manual override: force the tenant to an EXACT journey stage.
   *
   * The journey is derived (src/lib/tenantJourney.ts), so "setting a stage"
   * means writing the real signals the resolver reads until it lands exactly on
   * the target — booking columns (status / contract / key timestamps), the
   * deposit-paid payment row, and the handover-protocol submission state. No
   * emails or contracts are sent. Every write records a revert so the whole jump
   * is a single Undo. Backward moves downgrade records (paid→pending,
   * submitted→draft) rather than deleting them.
   */
  const forceStage = (target: TenantJourneyStageId) => {
    const spec = STAGE_FORCE_SPECS[target];
    const prevOverride = depositPaidOverride;
    // Revert thunks, applied in reverse on Undo.
    const reverts: Array<() => Promise<void>> = [];

    // Deposit rows fabricated or flipped by a stage override carry this marker
    // so a later audit can tell them apart from a genuinely collected deposit
    // (both otherwise render as a paid "Security deposit" row feeding reports).
    const OVERRIDE_TAG = "[admin stage override]";
    const taggedDescription = (existing: string | null | undefined) => {
      const base = (existing ?? "").trim() || "Security deposit";
      return base.includes(OVERRIDE_TAG) ? base : `${base} ${OVERRIDE_TAG}`;
    };

    // Same rule as `markDepositPaid`: a bare `status = 'paid'` is a claim the
    // deposit gate ignores, so a stage forced past Deposit pending would snap
    // straight back. The attestation (`marked_paid_*`) is what the predicate
    // accepts — and here it also records which admin performed the override.
    const attestPaid = () => ({
      status: "paid",
      paid_at: new Date().toISOString(),
      marked_paid_at: new Date().toISOString(),
      marked_paid_by: currentUserId ?? null,
    });

    const setDepositPaid = async () => {
      const liveDeposits = payments.filter(
        (p: any) =>
          p.type === "deposit" &&
          !["canceled", "failed"].includes(String(p.status ?? "").toLowerCase())
      );
      // Same reason as `markDepositPaid`: a single live row carrying less than
      // the contractual deposit (classically €0) keeps the gate shut however
      // often it is attested, so the forced stage would snap straight back on
      // the next refresh.
      const shortRowCents = (_row: any) =>
        depositTopUpCents({
          liveDepositRows: liveDeposits,
          expectedCents: depositState.expectedCents,
        });
      const unpaidRow = liveDeposits.find((p: any) => p.status !== "paid");
      if (unpaidRow) {
        const prev = {
          status: unpaidRow.status ?? "pending",
          paid_at: unpaidRow.paid_at ?? null,
          marked_paid_at: unpaidRow.marked_paid_at ?? null,
          marked_paid_by: unpaidRow.marked_paid_by ?? null,
          amount_cents: Number(unpaidRow.amount_cents) || 0,
        };
        const topUp = shortRowCents(unpaidRow);
        const { error } = await supabase
          .from("payments")
          .update({
            ...attestPaid(),
            ...(topUp != null ? { amount_cents: topUp } : {}),
          })
          .eq("id", unpaidRow.id);
        if (error) throw error;
        reverts.push(async () => {
          await supabase.from("payments").update(prev).eq("id", unpaidRow.id);
        });
      } else if (liveDeposits.length === 0) {
        const cents = Math.round((Number(booking.deposit_amount) || 0) * 100);
        if (cents <= 0)
          throw new Error(
            t(
              "admin.tenantProfile.toastSetDepositBeforeAdvance",
              "Set a deposit amount on the booking before advancing past Deposit pending."
            )
          );
        const { data, error } = await supabase
          .from("payments")
          .insert(
            operatorStamped<"payments">({
              booking_id: bookingId,
              // See markDepositPaid: a deposit row with no user_id never reaches
              // the tenant's Dashboard (RLS `auth.uid() = user_id`).
              user_id: (booking as any).user_id ?? userId,
              type: "deposit",
              amount_cents: cents,
              currency: "EUR",
              due_date: booking.move_in_date || booking.start_date || null,
              description: taggedDescription(null),
              ...attestPaid(),
            })
          )
          .select("id")
          .single();
        if (error) throw error;
        reverts.push(async () => {
          await supabase.from("payments").delete().eq("id", data.id);
        });
      } else {
        // Rows exist and all already read "paid", but unattested and unproven —
        // so the gate ignores them and the forced stage wouldn't hold. Attest
        // them, which is exactly what the override asserts.
        for (const row of liveDeposits) {
          const prev = {
            marked_paid_at: row.marked_paid_at ?? null,
            marked_paid_by: row.marked_paid_by ?? null,
            amount_cents: Number(row.amount_cents) || 0,
          };
          const topUp = shortRowCents(row);
          const { error } = await supabase
            .from("payments")
            .update({
              marked_paid_at: new Date().toISOString(),
              marked_paid_by: currentUserId ?? null,
              ...(topUp != null ? { amount_cents: topUp } : {}),
            })
            .eq("id", row.id);
          if (error) throw error;
          reverts.push(async () => {
            await supabase.from("payments").update(prev).eq("id", row.id);
          });
        }
      }
    };

    const setDepositUnpaid = async () => {
      const paidRows = payments.filter(
        (p: any) => p.type === "deposit" && p.status === "paid"
      );
      for (const row of paidRows) {
        // Flipping a genuinely-paid deposit back to pending would otherwise be
        // invisible; tag the description so the override is auditable, and keep
        // the original in `prev` so Undo restores it verbatim.
        const prev = {
          status: row.status,
          paid_at: row.paid_at ?? null,
          description: row.description ?? null,
          marked_paid_at: row.marked_paid_at ?? null,
          marked_paid_by: row.marked_paid_by ?? null,
        };
        const { error } = await supabase
          .from("payments")
          .update({
            status: "pending",
            paid_at: null,
            // Withdraw the attestation with the payment — leaving it behind
            // would re-satisfy the deposit gate the moment any other flow set
            // this row back to "paid".
            marked_paid_at: null,
            marked_paid_by: null,
            description: taggedDescription(row.description),
          })
          .eq("id", row.id);
        if (error) throw error;
        reverts.push(async () => {
          await supabase.from("payments").update(prev).eq("id", row.id);
        });
      }
      if (booking.manually_marked_paid) {
        await updateBookingRow(
          { manually_marked_paid: false },
          "deposit_marked_unpaid"
        );
        reverts.push(() =>
          updateBookingRow(
            { manually_marked_paid: true },
            "deposit_marked_unpaid_undone"
          )
        );
      }
    };

    // Downgrade any submitted protocol of a kind back to draft so the resolver
    // stops treating that leg as done.
    const downgradeProtocol = async (kind: "move_in" | "move_out") => {
      const submitted = protocolBundles.filter(
        (b: any) => b.kind === kind && b.status === "submitted"
      );
      for (const p of submitted) {
        const prev = { status: p.status, submitted_at: p.submitted_at ?? null };
        const { error } = await supabase
          .from("handover_protocols")
          .update({ status: "draft", submitted_at: null })
          .eq("id", p.id);
        if (error) throw error;
        reverts.push(async () => {
          await supabase.from("handover_protocols").update(prev).eq("id", p.id);
        });
      }
      // COL-427: a submission is one of the three things that ends an
      // indefinite reopen (see the reopen/close columns' comments) — the
      // reopened/manually-closed stamps don't get cleared at submission time
      // itself (the tenant's own `useSubmitProtocol` write has no admin
      // privilege to touch them), so undoing that submission here would
      // otherwise silently resurrect a stale "reopened" card with no admin
      // ever clicking reopen again. Clear both stamps for this kind whenever
      // there was something to downgrade.
      if (submitted.length) {
        const prevReopenedAt =
          (booking as any)[`${kind}_protocol_reopened_at`] ?? null;
        const prevManuallyClosedAt =
          (booking as any)[`${kind}_protocol_manually_closed_at`] ?? null;
        if (prevReopenedAt || prevManuallyClosedAt) {
          await updateBookingRow(
            {
              [`${kind}_protocol_reopened_at`]: null,
              [`${kind}_protocol_manually_closed_at`]: null,
            },
            `${kind}_protocol_reopen_stamp_cleared_on_downgrade`
          );
          reverts.push(() =>
            updateBookingRow(
              {
                [`${kind}_protocol_reopened_at`]: prevReopenedAt,
                [`${kind}_protocol_manually_closed_at`]: prevManuallyClosedAt,
              },
              `${kind}_protocol_reopen_stamp_cleared_on_downgrade_undone`
            )
          );
        }
      }
    };

    const submitMoveOutProtocol = async () => {
      const existing = protocolBundles.find((b: any) => b.kind === "move_out");
      if (existing) {
        if (existing.status === "submitted") return;
        const prev = {
          status: existing.status,
          submitted_at: existing.submitted_at ?? null,
        };
        const { error } = await supabase
          .from("handover_protocols")
          .update({
            status: "submitted",
            submitted_at: new Date().toISOString(),
          })
          .eq("id", existing.id);
        if (error) throw error;
        reverts.push(async () => {
          await supabase
            .from("handover_protocols")
            .update(prev)
            .eq("id", existing.id);
        });
      } else {
        const { data, error } = await supabase
          .from("handover_protocols")
          .insert({
            // `ref` is NOT NULL + UNIQUE with no DB default — generate one the
            // same way the tenant-facing protocol flow does.
            ref: generateProtocolRef(),
            booking_id: bookingId,
            user_id: (booking as any).user_id ?? userId,
            kind: "move_out",
            status: "submitted",
            submitted_at: new Date().toISOString(),
          })
          .select("id")
          .single();
        if (error) throw error;
        reverts.push(async () => {
          await supabase.from("handover_protocols").delete().eq("id", data.id);
        });
      }
    };

    return runAction(
      async () => {
        try {
          if (!bookingId) throw new Error("No booking.");

          // The contract gates are evidence-based (hasContractDocument): a
          // forced status/contract_status can no longer stand in for a missing
          // document, so refuse the jump instead of writing columns that leave
          // the resolver stuck at Applied anyway. `moved_out` is exempt — a
          // terminal status is an exit, not a climb up the ladder.
          const targetIdx = tenantJourneyStageIndex(target);
          if (
            targetIdx >= tenantJourneyStageIndex("signature_pending") &&
            targetIdx < tenantJourneyStageIndex("moved_out") &&
            !hasContractDocument(booking)
          ) {
            throw new Error(
              t(
                "admin.tenantProfile.toastContractBeforeAdvance",
                "Generate or upload a contract before advancing past Applied — the stage ladder needs the document on file, not just the status."
              )
            );
          }

          // Deposit signal.
          if (spec.depositPaid) await setDepositPaid();
          else await setDepositUnpaid();

          // Move-in protocol: stages before Active must not read as move-in-done.
          if (!spec.allowMoveInProto) await downgradeProtocol("move_in");

          // Move-out protocol: only Key-return-pending needs it submitted.
          if (spec.moveOutProtoSubmitted) await submitMoveOutProtocol();
          else await downgradeProtocol("move_out");

          // Booking columns last, so the visible status reflects the final state.
          // The exact column set (incl. clearing contract artifacts for Applied)
          // comes from the shared, unit-tested spec.
          const patch = bookingColumnsForStage(
            target,
            booking,
            new Date().toISOString()
          );
          const prevCols: Record<string, any> = {};
          for (const k of Object.keys(patch))
            prevCols[k] = (booking as any)[k] ?? null;
          await updateBookingRow(patch, "stage_forced");
          reverts.push(() => updateBookingRow(prevCols, "stage_forced_undone"));

          setDepositPaidOverride(spec.depositPaid);
        } catch (e) {
          // A multi-write jump that fails partway would otherwise leave the
          // booking in a mixed half-forced state with no Undo offered (runAction
          // only surfaces Undo on success). Best-effort unwind everything that
          // already succeeded, in reverse, before rethrowing — each revert is
          // guarded so one failure doesn't strand the rest.
          for (const r of [...reverts].reverse()) {
            try {
              await r();
            } catch {
              /* keep unwinding the remaining reverts */
            }
          }
          setDepositPaidOverride(prevOverride);
          throw e;
        }
      },
      t("admin.tenantJourney.stageSetToToast", "Stage set to {stage}").replace(
        "{stage}",
        tenantJourneyStageLabel(t, target)
      ),
      async () => {
        for (const r of [...reverts].reverse()) await r();
        setDepositPaidOverride(prevOverride);
      }
    );
  };

  /* -------------------- uploads (with mandatory handover note) -------------------- */

  const contractInputRef = useRef<HTMLInputElement>(null);

  /** Validate the picked file and stage it — the note modal handles the rest. */
  function pickUpload(
    file: File | undefined,
    kind: "contract" | "key-pickup" | "key-return"
  ) {
    if (!file) return;
    if (kind === "contract") {
      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        toast.error(
          t(
            "admin.tenantProfile.toastUploadPdfOnly",
            "Please upload a PDF file."
          )
        );
        return;
      }
    }
    setPendingUpload({ file, kind });
  }

  /** Upload a file to the tenant-documents bucket and return its metadata. */
  async function uploadToTenantDocs(file: File, scope: string) {
    const safeName = file.name.replace(/[^\w.-]+/g, "_");
    const key = `admin-notes/booking-${bookingId}/${scope}-${Date.now()}-${safeName}`;
    const { error: upErr } = await supabase.storage
      .from("tenant-documents")
      .upload(key, file, {
        contentType: file.type || "application/octet-stream",
        upsert: false,
      });
    if (upErr) throw upErr;
    const { data } = supabase.storage
      .from("tenant-documents")
      .getPublicUrl(key);
    return {
      key,
      url: data.publicUrl,
      name: file.name,
      size: file.size,
      mimeType: file.type || null,
    };
  }

  /**
   * Complete the staged upload with its mandatory note. Contract PDFs go to the
   * `contracts` bucket and flip the booking to signed; key documents attach to a
   * tenant-profile note. In both cases the note is persisted as an admin note so
   * it "persists platform-wide".
   */
  async function confirmPendingUpload(note: string) {
    if (!pendingUpload || !bookingId) return;
    const { file, kind } = pendingUpload;
    setBusy(true);
    try {
      if (kind === "contract") {
        const fileName = `signed-contracts/admin-booking-${bookingId}-${Date.now()}.pdf`;
        const { error: upErr } = await supabase.storage
          .from("contracts")
          .upload(fileName, file, {
            contentType: "application/pdf",
            upsert: false,
          });
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage
          .from("contracts")
          .getPublicUrl(fileName);
        const currentStatus = String(booking.contract_status ?? "");
        const patch: Record<string, unknown> = {
          signed_contract_url: urlData.publicUrl,
          manual_contract_upload: true,
          contract_signed_at: new Date().toISOString(),
          // A custom card name belongs to the file it was given to.
          contract_display_name: null,
        };
        if (currentStatus !== "approved" && currentStatus !== "signed") {
          patch.contract_status = "approved";
        }
        const { error } = await supabase
          .from("bookings")
          .update(patch)
          .eq("id", bookingId);
        if (error) throw error;
        patchBooking(patch);
        await addNote.mutateAsync({
          // 'handover' keeps the upload audit trail in Documents and the
          // Activity Timeline, and out of the notes feed — it is a record of a
          // file arriving, not a remark anybody wrote.
          kind: "handover",
          body: `Signed contract uploaded.\n${note}`,
          bookingId,
          userId,
          createdBy: currentUserId ?? "",
        });
        toast.success(
          t(
            "admin.tenantProfile.toastSignedContractUploaded",
            "Signed contract uploaded."
          )
        );
        // Owner "New Lease Agreement Signed" mail — fire-and-forget, same
        // shape as the other owner-lifecycle invokes on this dialog.
        void supabase.functions
          .invoke("notify-owner-lifecycle", {
            body: { event: "lease_signed", bookingId },
          })
          .catch(() => {});
      } else {
        const meta = await uploadToTenantDocs(
          file,
          kind === "key-pickup" ? "key-pickup" : "key-return"
        );
        try {
          await addNote.mutateAsync({
            kind: "handover",
            // Persisted note body — keep language-invariant.
            body: `${kind === "key-pickup" ? "Key pickup" : "Key return"} document.\n${note}`,
            bookingId,
            userId,
            createdBy: currentUserId ?? "",
            file: meta,
          });
        } catch (noteErr) {
          // The file is already in storage; if the note row fails to persist,
          // remove the orphaned object so it doesn't linger unreferenced.
          await supabase.storage
            .from("tenant-documents")
            .remove([meta.key])
            .catch(() => {});
          throw noteErr;
        }
        toast.success(
          t(
            "admin.tenantProfile.toastDocUploadedProfile",
            "Document uploaded to the tenant profile."
          )
        );
      }
      setPendingUpload(null);
      refreshAfterWrite();
    } catch (e) {
      const _m = actionError(e);
      setActionErr(_m);
      toast.error(_m);
    } finally {
      setBusy(false);
      if (contractInputRef.current) contractInputRef.current.value = "";
    }
  }

  /* -------------------- document upload (Documents tab) -------------------- */

  /**
   * Upload a file to the tenant's Documents section: stores it in the
   * tenant-documents bucket and inserts a `tenant_documents` row so it shows in
   * the Documents tab (and the tenant's own document list). `docType` must be
   * one of the tenant_documents.document_type CHECK values.
   */
  async function uploadDocument(file: File, docType: string) {
    if (!bookingId && !userId) {
      toast.error(
        t(
          "admin.tenantProfile.toastNoTenantForDoc",
          "No tenant to attach the document to."
        )
      );
      return;
    }
    setBusy(true);
    setActionErr(null);
    try {
      const safe = file.name.replace(/[^\w.-]+/g, "_");
      const key = `documents/booking-${bookingId ?? "na"}/${Date.now()}-${safe}`;
      const { error: upErr } = await supabase.storage
        .from("tenant-documents")
        .upload(key, file, {
          contentType: file.type || "application/octet-stream",
          upsert: false,
        });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage
        .from("tenant-documents")
        .getPublicUrl(key);
      const { error } = await supabase.from("tenant_documents").insert(
        operatorStamped<"tenant_documents">({
          user_id: userId,
          booking_id: bookingId,
          uploaded_by: currentUserId,
          // Admin-area upload: the owner portal must not read it as the tenant's.
          source: "admin",
          document_type: docType,
          title: file.name,
          file_url: urlData.publicUrl,
          file_name: file.name,
          mime_type: file.type || null,
          status: "pending",
        })
      );
      if (error) throw error;
      toast.success(
        t("admin.tenantProfile.toastDocUploaded", "Document uploaded.")
      );
      void queryClient.invalidateQueries({
        queryKey: ["v2", "tenant-documents"],
      });
    } catch (e) {
      const msg = actionError(e);
      setActionErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Rename an uploaded tenant document (updates the display file name).
   * An admin_notes attachment (e.g. a key handover photo) resolves to a
   * different backing table than a tenant_documents row — see
   * resolveDocTarget — but shares the same rename affordance in the
   * Documents tab.
   */
  async function renameDocument(docId: string, newName: string): Promise<void> {
    const name = newName.trim();
    if (!docId || !name) return;
    setBusy(true);
    setActionErr(null);
    try {
      const { table, rowId: noteId } = resolveDocTarget(docId);
      if (table === "bookings") {
        // The generated contract card: display name only. The file, its URL
        // and the contract status are not touched (COL-588).
        if (!bookingId)
          throw new Error(
            t("admin.tenantProfile.toastNoBooking", "No booking.")
          );
        const prevName =
          String(booking.contract_display_name ?? "").trim() ||
          "Lease contract.pdf";
        const { error } = await supabase
          .from("bookings")
          .update({ contract_display_name: name })
          .eq("id", bookingId);
        if (error) throw error;
        patchBooking({ contract_display_name: name });
        await logAudit({
          action: "tenant_document_renamed",
          // Filed like every other document rename so the Activity Timeline
          // titles it; booking_id in the metadata is what scopes it here.
          resourceType: "tenant_document",
          resourceId: `${CONTRACT_DOC_ID}:${bookingId}`,
          oldValues: { file_name: prevName },
          newValues: { file_name: name },
          metadata: { file_name: name },
        });
        toast.success(
          t("admin.tenantProfile.toastDocRenamed", "Document renamed.")
        );
        refreshAfterWrite();
        return;
      }
      if (table === "admin_notes") {
        const prevName =
          adminNotes.find((n: any) => String(n.id) === noteId)?.file_name ??
          null;
        const { error } = await supabase
          .from("admin_notes")
          .update({ file_name: name })
          .eq("id", noteId);
        if (error) throw error;
        await logAudit({
          action: "tenant_document_renamed",
          resourceType: "tenant_document",
          resourceId: noteId,
          oldValues: { file_name: prevName },
          newValues: { file_name: name },
          metadata: { file_name: name },
        });
        toast.success(
          t("admin.tenantProfile.toastDocRenamed", "Document renamed.")
        );
        refreshAfterWrite();
        return;
      }
      const prevName =
        documents.find((d: any) => String(d.id) === String(docId))?.file_name ??
        null;
      const { error } = await supabase
        .from("tenant_documents")
        .update({ file_name: name, updated_at: new Date().toISOString() })
        .eq("id", docId);
      if (error) throw error;
      await logAudit({
        action: "tenant_document_renamed",
        resourceType: "tenant_document",
        resourceId: docId,
        oldValues: { file_name: prevName },
        newValues: { file_name: name },
        metadata: { file_name: name },
      });
      toast.success(
        t("admin.tenantProfile.toastDocRenamed", "Document renamed.")
      );
      refreshAfterWrite();
    } catch (e) {
      const m = actionError(e);
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Change the label (`tenant_documents.document_type`) on an uploaded
   * document — the same set the upload dropdown picks from, plus any custom
   * labels an admin has created. Admin-notes attachments have no
   * document_type column, so this only ever targets a real tenant_documents
   * row (see resolveDocTarget / the `editable` gate in the Documents tab).
   */
  async function editDocumentLabel(
    docId: string,
    docType: string
  ): Promise<void> {
    if (!docId || !docType) return;
    setBusy(true);
    setActionErr(null);
    try {
      const prevType =
        documents.find((d: any) => String(d.id) === String(docId))
          ?.document_type ?? null;
      const { error } = await supabase
        .from("tenant_documents")
        .update({
          document_type: docType,
          updated_at: new Date().toISOString(),
        })
        .eq("id", docId);
      if (error) throw error;
      await logAudit({
        action: "tenant_document_relabelled",
        resourceType: "tenant_document",
        resourceId: docId,
        oldValues: { document_type: prevType },
        newValues: { document_type: docType },
        metadata: { document_type: docType },
      });
      toast.success(
        t("admin.tenantProfile.toastDocRelabelled", "Document label updated.")
      );
      refreshAfterWrite();
    } catch (e) {
      const m = actionError(e);
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Rename an internal note's attachment (`admin_notes.file_name`).
   *
   * No audit row: the notes table is its own record, and the trigger that
   * guards this UPDATE already refuses anyone but the author or a super admin —
   * whose message is what the toast shows when it does.
   */
  async function renameNoteAttachment(
    noteId: string,
    newName: string
  ): Promise<void> {
    const fileName = newName.trim();
    if (!noteId || !fileName) return;
    setBusy(true);
    setActionErr(null);
    try {
      await renameAttachment.mutateAsync({ id: noteId, fileName });
      toast.success(
        t("admin.tenantProfile.toastAttachmentRenamed", "Attachment renamed.")
      );
    } catch (e) {
      const m = actionError(e);
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Save (or clear) the note on a tenant document. The note lives on
   * `tenant_documents.notes` and is rendered on the document's card in the
   * Documents tab; the audit row (tagged with this booking) is what makes it
   * surface as its own event in the Overview Activity timeline — without it a
   * saved note left no trace anywhere the admin could see.
   */
  async function saveDocumentNote(docId: string, note: string): Promise<void> {
    if (!docId) return;
    const next = note.trim() || null;
    const doc = documents.find((d: any) => String(d.id) === String(docId));
    const prev = (doc?.notes as string | null | undefined)?.trim() || null;
    if (next === prev) {
      toast.info(
        t("admin.tenantProfile.toastNoteUnchanged", "Note unchanged.")
      );
      return;
    }
    setBusy(true);
    setActionErr(null);
    try {
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from("tenant_documents")
        .update({
          notes: next,
          // Stored attribution, so the by-line survives independently of the
          // audit log (which only covers notes saved after auditing existed).
          notes_updated_at: next == null ? null : nowIso,
          notes_updated_by: next == null ? null : (currentUserId ?? null),
          updated_at: nowIso,
        })
        .eq("id", docId);
      if (error) throw error;
      await logAudit({
        action:
          next == null
            ? "tenant_document_note_cleared"
            : prev == null
              ? "tenant_document_note_added"
              : "tenant_document_note_updated",
        resourceType: "tenant_document",
        resourceId: docId,
        oldValues: { notes: prev },
        newValues: { notes: next },
        metadata: {
          file_name: doc?.file_name ?? doc?.title ?? null,
        },
      });
      toast.success(
        next == null
          ? t(
              "admin.tenantProfile.toastNoteRemoved",
              "Note removed from the document."
            )
          : t(
              "admin.tenantProfile.toastNoteSaved",
              "Note saved to the document — visible on its card and in the Activity timeline."
            )
      );
      refreshAfterWrite();
    } catch (e) {
      const m = actionError(e);
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Delete a tenant document with a required reason. The reason is only ever
   * useful if it's guaranteed to be recorded, so the audit row is written
   * FIRST — and its error checked explicitly, since the supabase client
   * returns `{ error }` rather than throwing, so a swallowed error here would
   * let the document disappear with no reason on record. A tenant_documents
   * file is removed before the audit and the row delete: when storage keeps
   * it (refused or failed), the row stays and the admin sees why, instead of
   * a deleted record whose file is still reachable at its old URL.
   */
  async function deleteDocument(
    doc: { id: string; file_url?: string | null; file_name?: string | null },
    reason: string
  ): Promise<void> {
    if (!doc?.id) return;
    // The generated contract card is rename-only: there is no document row
    // behind it, and the booking's contract is not ours to delete from here.
    if (resolveDocTarget(doc.id).table === "bookings") return;
    setBusy(true);
    setActionErr(null);
    try {
      const { table, rowId } = resolveDocTarget(doc.id);

      async function writeDeleteAudit(): Promise<void> {
        const { error: auditError } = await supabase.from("audit_logs").insert({
          user_id: currentUserId ?? null,
          action: "tenant_document_deleted",
          resource_type: "tenant_document",
          resource_id: rowId,
          old_values: { file_name: doc.file_name ?? null },
          new_values: null,
          metadata: {
            reason: reason.trim() || null,
            file_name: doc.file_name ?? null,
            booking_id: bookingId,
            source: "tenant_profile",
          },
        });
        if (auditError) throw auditError;
      }

      // Derive the storage key from the file URL (public or signed) and remove
      // the object. Strip any query string (signed URLs carry `?token=...`)
      // before deriving the key, and check the result — `.remove()` resolves
      // with `{ error }` rather than throwing, so an unchecked call can't tell
      // a real failure from a no-op. Handover attachments (kind != 'general')
      // live in this same public bucket as tenant_documents uploads, so the
      // marker matches for them too; an internal note's file_url points at
      // the private `admin-notes` bucket instead, so the marker naturally
      // never matches and this is a no-op for those (nothing to remove there
      // anyway — see the admin_notes branch below).
      //
      // Null once the file is gone, otherwise why it is still stored. Storage
      // refuses a delete (COL-550: only staff or the uploader) without an
      // error, by removing nothing; removeStoredFile tells that apart from a
      // file that was already gone.
      async function removePublicStorageObject(): Promise<string | null> {
        const marker = "/tenant-documents/";
        const idx = doc.file_url ? doc.file_url.indexOf(marker) : -1;
        if (idx < 0 || !doc.file_url) return null;
        const rawKey = doc.file_url.slice(idx + marker.length).split("?")[0];
        try {
          const gone = await removeStoredFile({
            bucket: "tenant-documents",
            path: decodeURIComponent(rawKey),
          });
          return gone ? null : "access denied";
        } catch (storageError) {
          return actionError(storageError);
        }
      }

      if (table === "admin_notes") {
        // Mirror the Notes section's own delete (`useDeleteNote`): a soft
        // retraction (deleted_at + deleted_reason), not a hard DELETE.
        // `admin_notes_guard_authorship` polices this the same way it does
        // for the Notes section, and it matters here for two reasons a hard
        // DELETE would break: (1) the row may be a thread root — replies
        // reference it via `parent_id ... ON DELETE CASCADE`, so a hard
        // delete would silently destroy other admins' replies along with the
        // attachment; (2) `admin_notes_delete_needs_reason` and the retracted
        // row itself are how the reason survives for later reference, which
        // a physically-deleted row cannot do.
        //
        // The retraction (and its affected-row check) runs BEFORE the audit
        // insert, unlike the tenant_documents branch below: the guard trigger
        // actively RAISES for an admin who isn't the note's author or a super
        // admin, and writing the audit row first would leave a "deleted"
        // Activity Timeline entry for a document that's still there.
        const { data, error } = await supabase
          .from("admin_notes")
          .update({
            deleted_at: new Date().toISOString(),
            // Persisted in admin_notes.deleted_reason — keep language-invariant.
            deleted_reason:
              reason.trim() || "Deleted from tenant Documents tab.",
          })
          .eq("id", rowId)
          .select("id");
        if (error) throw error;
        if (!data || data.length === 0) {
          throw new Error(
            t(
              "admin.tenantProfile.toastDocNotDeleted",
              "The document was not deleted."
            )
          );
        }
        // A handover attachment's public file should stop being reachable at
        // its old URL even though the row survives (retracted, not gone) —
        // only its private-bucket internal-note counterpart has nothing here
        // to remove.
        const stillStored = await removePublicStorageObject();
        if (stillStored) {
          toast.error(
            `Document record deleted, but the file is still in storage (removal failed: ${stillStored}). Remove it manually.`
          );
        }
        await writeDeleteAudit();
      } else {
        // The file goes first, so a delete storage refuses keeps the row
        // rather than leaving the file behind a deleted record. The audit
        // row still comes before the row delete: the reason is only ever
        // useful if it's guaranteed to be recorded, and the supabase client
        // returns `{ error }` rather than throwing, so a swallowed error
        // would let the document disappear with no reason on record.
        const stillStored = await removePublicStorageObject();
        if (stillStored) {
          throw new Error(
            `${t("admin.tenantProfile.toastDocNotDeleted", "The document was not deleted.")} (${stillStored})`
          );
        }
        await writeDeleteAudit();
        const { error } = await supabase.from(table).delete().eq("id", rowId);
        if (error) throw error;
      }

      toast.success(
        table === "admin_notes"
          ? t(
              "admin.tenantProfile.toastDocDeletedWithHistory",
              "Document deleted (note and key-handover history entry removed too)."
            )
          : t("admin.tenantProfile.toastDocDeleted", "Document deleted.")
      );
      refreshAfterWrite();
    } catch (e) {
      const m = actionError(e);
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  /* -------------------- payment proofs -------------------- */

  const proofInputRef = useRef<HTMLInputElement>(null);
  const [proofTarget, setProofTarget] = useState<string | null>(null);

  function pickProof(paymentId: string) {
    setProofTarget(paymentId);
    proofInputRef.current?.click();
  }

  /** Upload a deposit proof even when no deposit payment row exists yet —
   *  creates a (pending) deposit row first, then attaches the proof to it. */
  async function pickDepositProof() {
    let depositId = payments.find((p: any) => p.type === "deposit")?.id;
    if (!depositId) {
      if (!bookingId) return;
      // Refuse to mint a €0 deposit row — the same guard the two "mark paid"
      // paths already carry. `deposit_amount` filled in AFTER the upload does
      // not flow back into the row, so a €0 row leaves the deposit permanently
      // short of the contractual amount: the gate stays shut, the tenant sticks
      // at Deposit pending, and no admin action clears it.
      const cents = Math.round((Number(booking.deposit_amount) || 0) * 100);
      if (cents <= 0) {
        const m =
          "Set the deposit amount on the booking before uploading a deposit proof.";
        setActionErr(m);
        toast.error(m);
        return;
      }
      const { data, error } = await supabase
        .from("payments")
        .insert(
          operatorStamped<"payments">({
            booking_id: bookingId,
            // See markDepositPaid: a deposit row with no user_id never reaches
            // the tenant's Dashboard (RLS `auth.uid() = user_id`) — and an unpaid
            // one billed this way is exactly what the tenant is meant to pay.
            user_id: (booking as any).user_id ?? userId,
            type: "deposit",
            amount_cents: cents,
            currency: "EUR",
            status: "pending",
            due_date: booking.move_in_date || booking.start_date || null,
            description: "Security deposit",
          })
        )
        .select("id")
        .single();
      if (error) {
        const m = actionError(error);
        setActionErr(m);
        toast.error(m);
        return;
      }
      depositId = data.id;
      refreshAfterWrite();
    }
    setProofTarget(String(depositId));
    proofInputRef.current?.click();
  }

  function viewProof(proof: { file_path?: string | null } | null) {
    if (!proof?.file_path) return;
    // Every proof opens through a signed URL: bank-transaction proofs live in
    // the private bank-proofs bucket, and contracts is going private (COL-436).
    void openProofInNewTab(proof.file_path).then((ok) => {
      if (!ok)
        toast.error(t("admin.docs.openFailed", "Could not open the document."));
    });
  }

  async function uploadProofFile(file: File) {
    const paymentId = proofTarget;
    if (!paymentId) return;
    const maxSize = 10 * 1024 * 1024;
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];
    if (file.size > maxSize) {
      toast.error(
        t(
          "admin.tenantProfile.toastFileTooLarge",
          "File must be 10 MB or less."
        )
      );
      return;
    }
    if (!validTypes.includes(file.type)) {
      toast.error(
        t(
          "admin.tenantProfile.toastFileWrongType",
          "File must be a PDF or image (JPEG, PNG, WebP)."
        )
      );
      return;
    }
    setBusy(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const ext = file.name.split(".").pop() || "pdf";
      const safeName = file.name.replace(/\s+/g, "-");
      const path = `payment-proofs/${paymentId}/${Date.now()}-admin-${safeName}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("contracts")
        .upload(path, file, {
          contentType: file.type || "application/octet-stream",
          upsert: false,
        });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from("payment_proofs").insert({
        payment_id: paymentId,
        file_path: path,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        status: "approved",
        reviewed_at: new Date().toISOString(),
        reviewed_by: user?.id ?? null,
        // Persisted on the document row — keep language-invariant.
        admin_comment: "Uploaded by admin",
      });
      if (insErr) {
        await supabase.storage.from("contracts").remove([path]);
        throw insErr;
      }
      await logAudit({
        action: "payment_proof_uploaded",
        resourceType: "payment_proof",
        resourceId: String(paymentId),
        oldValues: null,
        newValues: { file_name: file.name },
        metadata: { payment_id: paymentId, file_name: file.name },
      });
      toast.success(
        t("admin.tenantProfile.toastProofUploaded", "Payment proof uploaded.")
      );
      refreshAfterWrite();
    } catch (e) {
      const _m = actionError(e);
      setActionErr(_m);
      toast.error(_m);
    } finally {
      setBusy(false);
      setProofTarget(null);
      if (proofInputRef.current) proofInputRef.current.value = "";
    }
  }

  /** Open the in-app confirmation for sending the next dunning reminder. */
  function requestSendReminder() {
    if (!bookingId) return;
    // Resolve the current invoice's concrete date from the ledger. The first
    // prorated rent is due on move-in day even though later rent recurs on the
    // 1st, so the recurring booking day alone can open this dialog too early.
    const currentInvoice = currentPeriodLedger(
      payments as Parameters<typeof currentPeriodLedger>[0],
      new Map([[bookingId, booking.rent_due_date ?? null]])
    ).get(bookingId);
    const stage = manualReminderStage({
      ...(booking as any),
      invoice_due_date: currentInvoice?.dueDate ?? null,
    });
    if (stage === 0) {
      toast.info(
        t(
          "admin.tenantProfile.toastNotReminderStage",
          "Not at a reminder stage yet — dunning starts 3 days after the due date, and only while the current month is unpaid."
        )
      );
      return;
    }
    setReminderStage(stage);
  }

  /** Send the next dunning reminder for an overdue rent payment. */
  async function sendReminder(nextStage: number) {
    if (!bookingId) return;
    setBusy(true);
    setActionErr(null);
    try {
      const stage = nextStage;
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token ?? "";
      const { error } = await supabase.functions.invoke("send-rent-reminder", {
        body: { bookingId, stage, email_lang: "de" },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (error) throw error;
      toast.success(
        t("admin.tenantProfile.toastReminderSent", "Reminder sent.")
      );
      refreshAfterWrite();
    } catch (e) {
      const m = actionError(e);
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  /** Collect one selected rent row via the tenant's SEPA mandate (feedback 5a). */
  async function chargeSepa(paymentId: string) {
    setBusy(true);
    setActionErr(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token ?? "";
      const { data, error } = await supabase.functions.invoke(
        "charge-rent-sepa",
        {
          body: { paymentId },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      if (error) throw error;
      toast.success(
        data?.status === "succeeded"
          ? t(
              "admin.tenantProfile.toastDebitCollected",
              "Direct debit collected."
            )
          : t(
              "admin.tenantProfile.toastDebitInitiated",
              "Direct debit initiated — settles in 2–5 business days."
            )
      );
      refreshAfterWrite();
    } catch (e) {
      // The edge function's own reason, not "non-2xx status code".
      const m = await edgeFunctionErrorMessage(
        e,
        t("admin.tenantProfile.toastDebitFailed", "Direct debit failed.")
      );
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  /** Decide a tenant's request to end their SEPA mandate (feedback 6). */
  async function decideDdCancellation(
    requestId: string,
    decision: "approve" | "reject"
  ) {
    setBusy(true);
    setActionErr(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token ?? "";
      const { error } = await supabase.functions.invoke("cancel-sepa-mandate", {
        body: { requestId, decision },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (error) throw error;
      toast.success(
        decision === "approve"
          ? t(
              "admin.tenantProfile.toastMandateEnded",
              "Mandate ended — direct debit is off for this tenant."
            )
          : t(
              "admin.tenantProfile.toastRequestRejected",
              "Request rejected — the mandate stays active."
            )
      );
      queryClient.invalidateQueries({
        queryKey: ["v2", "tenant-sepa-status", userId],
      });
      refreshAfterWrite();
    } catch (e) {
      const m = await edgeFunctionErrorMessage(
        e,
        decision === "approve"
          ? t(
              "admin.tenantProfile.toastEndMandateFailed",
              "Could not end the mandate."
            )
          : t(
              "admin.tenantProfile.toastRejectRequestFailed",
              "Could not reject the request."
            )
      );
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  /** Edit a rent/deposit payment's amount + due date. */
  async function editPayment(
    paymentId: string,
    patch: {
      amount_cents?: number;
      due_date?: string;
      status?: string;
      /** COL-621: the full rent on a kickback month; null clears it. */
      gross_amount_cents?: number | null;
    }
  ): Promise<void> {
    setBusy(true);
    setActionErr(null);
    try {
      const prev = payments.find(
        (p: any) => String(p.id) === String(paymentId)
      );
      // COL-621: recording, changing or removing a kickback moves the owner's
      // payout, which only an open month can still carry. So does moving a
      // kickback row to another month: both months must be open.
      const prevDue = (prev as any)?.due_date
        ? String((prev as any).due_date)
        : null;
      const nextDue = patch.due_date ?? prevDue;
      // A kickback row before or after this save (one being created counts).
      const kickbackRow =
        (prev as any)?.gross_amount_cents != null ||
        patch.gross_amount_cents != null;
      const movesMonth =
        !!prevDue &&
        !!nextDue &&
        prevDue.slice(0, 7) !== String(nextDue).slice(0, 7);
      if (
        bookingId &&
        nextDue &&
        ("gross_amount_cents" in patch || (kickbackRow && movesMonth))
      ) {
        await assertKickbackMonthOpen(bookingId, String(nextDue));
      }
      if (bookingId && prevDue && kickbackRow && movesMonth) {
        await assertKickbackMonthOpen(bookingId, prevDue);
      }
      // A status flip carries (or withdraws) the ADMIN ATTESTATION. Handled here
      // rather than at each call site — the row editor's t("admin.tenantProfile.actionMarkPaid", "Mark paid") checkbox
      // reaches this function too, and a bare `status = 'paid'` is a claim that
      // `isPaymentEffectivelyPaid` ignores: the row would render "Paid" while
      // the deposit gate and every rent rollup still counted it unpaid.
      // Stripe rows are excluded: the webhook decides those, and stamping an
      // admin on one would read as an off-platform settlement that never happened.
      const isStripeRow = !!(
        (prev as any)?.stripe_payment_intent_id ||
        (prev as any)?.stripe_charge_id
      );
      const write: Record<string, any> = { ...patch };
      if (patch.status !== undefined && !isStripeRow) {
        const paid = String(patch.status).toLowerCase() === "paid";
        write.marked_paid_at = paid ? new Date().toISOString() : null;
        write.marked_paid_by = paid ? (currentUserId ?? null) : null;
      }
      const { error } = await supabase
        .from("payments")
        .update(write)
        .eq("id", paymentId);
      if (error) throw error;
      // Owner "Rent/Deposit received" mail. This surface settles the row with
      // a direct UPDATE — no Stripe webhook and no edge function in the path —
      // so without this hop the owner is never told and the payment leaves no
      // trace on their profile at all (src/lib/owner-payment-notify.ts).
      // Only on the pending → paid transition: re-saving an already-paid row's
      // amount is not a new receipt, and un-marking one is not a receipt.
      if (
        String(patch.status ?? "").toLowerCase() === "paid" &&
        String(prev?.status ?? "").toLowerCase() !== "paid"
      ) {
        void reportOwnerPaymentNotification({
          id: String(paymentId),
          type: (prev as any)?.type ?? null,
          booking_id: (prev as any)?.booking_id ?? bookingId ?? null,
          amount_cents:
            patch.amount_cents ?? (prev as any)?.amount_cents ?? null,
          due_date: patch.due_date ?? (prev as any)?.due_date ?? null,
        });
      }
      await logAudit({
        action: "payment_edited",
        resourceType: "payment",
        resourceId: String(paymentId),
        // Audit `write`, not `patch` — the attestation is part of what changed,
        // and it's the field that answers "who said this was paid?".
        oldValues: prev
          ? Object.fromEntries(
              Object.keys(write).map((k) => [k, (prev as any)[k] ?? null])
            )
          : null,
        newValues: write,
        metadata: { payment_type: prev?.type ?? null },
      });
      toast.success(
        t("admin.tenantProfile.toastPaymentUpdated", "Payment updated.")
      );
      refreshAfterWrite();
    } catch (e) {
      const m =
        e instanceof RentKickbackClosedError
          ? t(
              "admin.tenantProfile.kickbackMonthClosed",
              "This month is already paid out or invoiced to the owner. A kickback can no longer be recorded or removed."
            )
          : actionError(e);
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Materialise a projected upcoming rent month into a real `payments` row so
   * it can be edited, carry a transfer proof and hold notes. Rent rows are
   * otherwise only created as each month comes due, which left the whole
   * upcoming schedule read-only.
   *
   * `unique_rent_per_booking_month` guarantees at most one live rent row per
   * booking-month — a 23505 here means another surface (or a concurrent click)
   * created the month first, so we adopt that row instead of failing.
   *
   * Returns the payment id, or null when nothing was created.
   */
  async function createScheduledRent(input: {
    dueDate: string;
    amountCents: number;
    markPaid?: boolean;
    /** COL-621: the full rent when the month is a kickback. */
    grossAmountCents?: number | null;
  }): Promise<string | null> {
    if (!bookingId) {
      toast.error(
        t(
          "admin.tenantProfile.toastNoBookingForRent",
          "No booking to schedule rent against."
        )
      );
      return null;
    }
    const dueDate = String(input.dueDate || "").slice(0, 10);
    const amountCents = Math.round(Number(input.amountCents));
    const grossAmountCents =
      input.grossAmountCents != null && input.grossAmountCents > amountCents
        ? Math.round(input.grossAmountCents)
        : null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
      toast.error(
        t("admin.tenantProfile.toastInvalidDueDate", "Pick a valid due date.")
      );
      return null;
    }
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      toast.error(
        t(
          "admin.tenantProfile.toastRentAmountTooLow",
          "Enter a rent amount greater than €0."
        )
      );
      return null;
    }
    setBusy(true);
    setActionErr(null);
    try {
      // COL-621: a kickback only on a month whose payout is still open.
      if (grossAmountCents != null) {
        await assertKickbackMonthOpen(bookingId, dueDate);
      }
      const status = input.markPaid ? "paid" : "scheduled";
      const row = {
        booking_id: bookingId,
        user_id: userId,
        type: "rent",
        amount_cents: amountCents,
        gross_amount_cents: grossAmountCents,
        currency: "EUR",
        status,
        paid_at: input.markPaid ? new Date().toISOString() : null,
        // "Mark paid" carries the admin attestation, same as the deposit action.
        // A bare `status = 'paid'` with no proof is ignored by
        // `isPaymentEffectivelyPaid`, so the row would render "Paid" while every
        // rent rollup still counted it unpaid.
        marked_paid_at: input.markPaid ? new Date().toISOString() : null,
        marked_paid_by: input.markPaid ? (currentUserId ?? null) : null,
        due_date: dueDate,
        description: `Rent ${dueDate.slice(0, 7)}`,
      };
      const { data, error } = await supabase
        .from("payments")
        .insert(operatorStamped<"payments">(row))
        .select("id")
        .single();

      let paymentId: string | null = data?.id ? String(data.id) : null;
      if (error) {
        if ((error as any)?.code !== "23505") throw error;
        const monthStart = `${dueDate.slice(0, 7)}-01`;
        const [y, m] = monthStart.split("-").map(Number);
        const nextStart =
          m === 12
            ? `${y + 1}-01-01`
            : `${y}-${String(m + 1).padStart(2, "0")}-01`;
        const { data: existing } = await supabase
          .from("payments")
          .select("id")
          .eq("booking_id", bookingId)
          .eq("type", "rent")
          .gte("due_date", monthStart)
          .lt("due_date", nextStart)
          .not("status", "in", "(canceled,failed)")
          .limit(1);
        paymentId = existing?.[0]?.id ? String(existing[0].id) : null;
        if (!paymentId) throw error;
        toast.info(
          t(
            "admin.tenantProfile.toastMonthAlreadyPaid",
            "This month already had a payment — opened that one."
          )
        );
        refreshAfterWrite();
        return paymentId;
      }
      if (!paymentId)
        throw new Error(
          t(
            "admin.tenantProfile.toastPaymentNotCreated",
            "Payment was not created."
          )
        );

      await logAudit({
        action: "payment_scheduled",
        resourceType: "payment",
        resourceId: paymentId,
        oldValues: null,
        newValues: {
          amount_cents: amountCents,
          ...(grossAmountCents != null
            ? { gross_amount_cents: grossAmountCents }
            : {}),
          due_date: dueDate,
          status,
        },
        metadata: { payment_type: "rent" },
      });
      // Same reasoning as editPayment above: a month recorded straight as paid
      // is a receipt the owner must hear about.
      if (input.markPaid) {
        void reportOwnerPaymentNotification({
          id: paymentId,
          type: "rent",
          booking_id: bookingId,
          amount_cents: amountCents,
          due_date: dueDate,
        });
      }
      toast.success(
        input.markPaid
          ? t("admin.tenantProfile.toastRentPaid", "Rent recorded as paid.")
          : t(
              "admin.tenantProfile.toastUpcomingRentSaved",
              "Upcoming rent saved — you can now attach a proof or a note."
            )
      );
      refreshAfterWrite();
      return paymentId;
    } catch (e) {
      const m =
        e instanceof RentKickbackClosedError
          ? t(
              "admin.tenantProfile.kickbackMonthClosed",
              "This month is already paid out or invoiced to the owner. A kickback can no longer be recorded or removed."
            )
          : actionError(e);
      setActionErr(m);
      toast.error(m);
      return null;
    } finally {
      setBusy(false);
    }
  }

  /** Set (or clear) the first-month rent override on the booking. */
  async function setFirstMonthOverride(euros: number | null): Promise<void> {
    // updateBookingRow audits the write itself, so there is no second
    // logAudit here — that produced two timeline rows for one action.
    await runAction(
      () =>
        updateBookingRow(
          { first_month_rent_override: euros },
          "first_month_rent_override_set"
        ),
      euros == null
        ? t(
            "admin.tenantProfile.toastFirstMonthCleared",
            "First-month override cleared"
          )
        : t(
            "admin.tenantProfile.toastFirstMonthOverridden",
            "First-month rent overridden"
          )
    );
  }

  /**
   * Set (or, with `null`, clear) the admin's rent due-day override (COL-410).
   * Routed through the `set_rent_due_day` RPC rather than a raw `bookings`
   * update: it audits itself and, in the same transaction, rewrites due_date
   * on this booking's future still-open rent rows so the ledger and the day
   * agree. That write also re-triggers `sync_booking_rent_due_day`, which is
   * what realigns every dunning/reminder surface to the new day — no
   * separate reminder logic to touch here.
   */
  async function setRentDueDay(day: number | null): Promise<void> {
    if (!bookingId) throw new Error("No booking.");
    if (day != null && (!Number.isInteger(day) || day < 1 || day > 31)) {
      toast.error(
        t(
          "admin.tenantProfile.toastInvalidDueDay",
          "Enter a due day between 1 and 31."
        )
      );
      return;
    }
    await runAction(
      async () => {
        const { error } = await (supabase as any).rpc("set_rent_due_day", {
          _booking_id: bookingId,
          _due_day: day,
        });
        if (error) throw error;
        // The RPC persists 1 (never NULL) for a reset — see the migration
        // header — so the optimistic patch must match, or it masks the real
        // value with a stale null until the next refetch.
        patchBooking({ rent_due_day_override: day ?? 1 });
      },
      day == null
        ? t(
            "admin.tenantProfile.toastDueDayReset",
            "Rent due day reset to the 1st (default)."
          )
        : `Rent due day set to the ${ordinalLabel(day)} — takes effect next month.`
    );
  }

  async function deleteProof(proof: { id: string; file_path?: string | null }) {
    setBusy(true);
    setActionErr(null);
    try {
      // The file goes first, so a delete storage refuses (COL-550: only staff
      // or the uploader) keeps the proof instead of leaving its file behind.
      const fileRef = proof.file_path
        ? storageRefFor(proof.file_path, proofBucket(proof.file_path))
        : null;
      if (fileRef && !(await removeStoredFile(fileRef))) {
        throw new Error(
          `${t("admin.tenantProfile.toastDocNotDeleted", "The document was not deleted.")} (access denied)`
        );
      }
      const { data: deletedRows, error } = await supabase
        .from("payment_proofs")
        .delete()
        .eq("id", proof.id)
        .select("id");
      if (error) throw error;
      if (!deletedRows?.length) {
        throw new Error(
          t(
            "admin.tenantProfile.toastDocNotDeleted",
            "The document was not deleted."
          )
        );
      }
      await logAudit({
        action: "payment_proof_deleted",
        resourceType: "payment_proof",
        resourceId: String(proof.id),
        oldValues: { file_path: proof.file_path ?? null },
        newValues: null,
      });
      toast.success(
        t("admin.tenantProfile.toastProofDeleted", "Proof deleted.")
      );
      refreshAfterWrite();
    } catch (e) {
      const m = actionError(e);
      setActionErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  const archived = !!booking.admin_archived_at;
  const archiveBooking = () =>
    runAction(
      () =>
        updateBookingRow(
          { admin_archived_at: archived ? null : new Date().toISOString() },
          archived ? "booking_restored" : "booking_archived"
        ),
      archived
        ? t(
            "admin.tenantProfile.toastBookingRestored",
            "Booking restored to pipeline"
          )
        : t("admin.tenantProfile.toastBookingArchived", "Booking archived"),
      () =>
        updateBookingRow(
          { admin_archived_at: archived ? new Date().toISOString() : null },
          archived ? "booking_archived" : "booking_restored"
        )
    );

  /** Save a standalone key-handover note (no file). */
  async function saveKeyNote(note: string): Promise<void> {
    if (!bookingId || !note.trim()) return;
    await addNote.mutateAsync({
      body: note.trim(),
      bookingId,
      userId,
      createdBy: currentUserId ?? "",
    });
    refreshAfterWrite();
  }

  /* -------------------- next action bar -------------------- */

  // Built every render on purpose (cheap). Memoising on `journey.stageId` alone
  // was a correctness bug: this dialog is a single persistent instance reused
  // across tenant switches, so a stale closure could fire contract-generation /
  // mark-signed against the PREVIOUS tenant's booking.
  const nextAction = (() => {
    const s = journey.stageId;
    const go = (tabId: TabId) => () => changeTab(tabId);
    const map: Partial<
      Record<
        TenantJourneyStageId,
        { label: string; hint: string; onClick: () => void }
      >
    > = {
      applied: {
        // Generating no longer sends: it produces a draft to review. Sending is
        // a separate, confirmed step in the Contract card, so the bar must not
        // promise an email it does not send.
        label: t("admin.tenantProfile.stepGenerateDraft", "Generate draft"),
        hint: t(
          "admin.tenantProfile.stepGenerateDraftDesc",
          "Generate the lease contract as a draft — nothing is sent to the tenant yet."
        ),
        onClick: () => void generateContract(),
      },
      signature_pending: {
        label: t("admin.tenantProfile.stepMarkSigned", "Mark contract signed"),
        hint: t(
          "admin.tenantProfile.stepMarkSignedDesc",
          "Verify and send the draft in the Contract card, then the tenant signs — or upload a signed PDF."
        ),
        onClick: () => void markContractSigned(),
      },
      deposit_pending: {
        label: t("admin.tenantProfile.stepGoPayments", "Go to Payments"),
        hint: t(
          "admin.tenantProfile.stepGoPaymentsDesc",
          "Mark the security deposit as paid on the Payments tab."
        ),
        onClick: go("payments"),
      },
      key_pickup_pending: {
        label: t("admin.tenantProfile.stepGoMoveIn", "Go to Move-in"),
        hint: t(
          "admin.tenantProfile.stepGoMoveInDesc",
          "Mark the key as picked up in the Keys section (Move-in)."
        ),
        onClick: go("movein"),
      },
      handover_pending: {
        label: t("admin.tenantProfile.stepGoMoveIn", "Go to Move-in"),
        hint: t(
          "admin.tenantProfile.stepMoveInProtocolDesc",
          "Tenant submits the move-in protocol to become Active."
        ),
        onClick: go("movein"),
      },
      moveout_scheduled: {
        label: t("admin.tenantProfile.stepGoMoveOut", "Go to Move-out"),
        hint: t(
          "admin.tenantProfile.stepGoMoveOutDesc",
          "Confirm the move-out date to continue."
        ),
        onClick: go("moveout"),
      },
      moveout_protocol_pending: {
        label: t("admin.tenantProfile.stepGoMoveOut", "Go to Move-out"),
        hint: t(
          "admin.tenantProfile.stepMoveOutProtocolDesc",
          "Tenant submits the move-out protocol."
        ),
        onClick: go("moveout"),
      },
      key_return_pending: {
        label: t("admin.tenantProfile.stepGoMoveOut", "Go to Move-out"),
        hint: t(
          "admin.tenantProfile.stepKeyReturnDesc",
          "Mark the key as returned to complete move-out."
        ),
        onClick: go("moveout"),
      },
      deposit_refund_pending: {
        label: t("admin.tenantProfile.stepGoMoveOut", "Go to Move-out"),
        hint: t(
          "admin.tenantProfile.stepSettleDepositDesc",
          "Settle the deposit and record the refund to finish the tenancy."
        ),
        onClick: go("moveout"),
      },
    };
    return map[s] ?? null;
  })();

  if (!tenant) return null;

  const st = journey.stage;

  const frame: React.CSSProperties = expanded
    ? {
        width: "100%",
        maxWidth: "100%",
        height: "100vh",
        borderRadius: 0,
      }
    : {
        width: "100%",
        maxWidth: 1140,
        height: "min(93vh,948px)",
        borderRadius: 28,
      };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            background: "rgba(9,15,28,.5)",
            backdropFilter: "blur(3px)",
          }}
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            // Radix's own dismiss for this layer runs unconditionally on
            // Escape — the notes drawer isn't a layer of its own (it's a
            // plain <aside>, not a portal), so without this the profile
            // closes right along with the drawer it was meant to contain.
            // NotesDrawer's own listener still fires and closes the drawer.
            if (notesDrawerOpen) e.preventDefault();
          }}
          aria-describedby={undefined}
          style={{
            position: "fixed",
            zIndex: 50,
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            // The gap between the overlay edge and the panel. A flat 34px cost a
            // phone 68px of a ~390px viewport, which is what pushed the tab bar
            // and the next-step buttons out of a panel that clips its overflow.
            padding: expanded ? 0 : "clamp(8px, 3vw, 34px)",
            width: "100%",
            maxWidth: expanded ? "100%" : 1208,
            height: expanded ? "100vh" : "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            data-tp=""
            style={{
              ...frame,
              position: "relative",
              background: "#fff",
              boxShadow:
                "0 40px 120px -30px rgba(9,15,28,.5),0 12px 40px -12px rgba(9,15,28,.25)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              fontFamily: "Inter,system-ui,-apple-system,'Segoe UI',sans-serif",
              color: "#0F172A",
            }}
          >
            <style>{`
              @keyframes tpActivePulse{0%,100%{box-shadow:0 0 0 3px rgba(16,185,129,.18)}50%{box-shadow:0 0 0 6px rgba(16,185,129,.05)}}
              @keyframes tpShimmer{0%{background-position:-320px 0}100%{background-position:320px 0}}
              .tp-skel{background:#EDEFF2;background-image:linear-gradient(90deg,#EDEFF2 0px,#F5F6F8 160px,#EDEFF2 320px);background-size:640px 100%;border-radius:8px;animation:tpShimmer 1.3s linear infinite}
              [data-tp] button:focus-visible,[data-tp] a:focus-visible,[data-tp] input:focus-visible,[data-tp] textarea:focus-visible{outline:2px solid #12B5A8;outline-offset:2px;border-radius:8px}
              /* This panel is styled inline, which cannot express a breakpoint.
                 Anything that genuinely needs one lives here, scoped to the
                 panel. The panel clips its overflow, so a row that refuses to
                 shrink does not scroll — it disappears. */
              @media (max-width:760px){
                /* Five tabs across a ~320px panel squeezed every label to one
                   word per line ("Move-" / "in") and clipped the last tab. Two
                   rows keeps all five readable and reachable without a swipe. */
                [data-tp] .tp-tabs{flex-wrap:wrap}
                [data-tp] .tp-tabs>button{flex:1 1 30%;min-width:92px;white-space:nowrap}
                /* Section padding: 28px a side is a third of a phone panel. */
                [data-tp] .tp-body{padding-left:16px;padding-right:16px}
                [data-tp] .tp-head{padding-left:16px;padding-right:16px}
                /* Once the next-step actions have a row to themselves, share it
                   evenly instead of leaving two ragged left-aligned buttons. */
                [data-tp] .tp-nextstep-actions{width:100%}
                [data-tp] .tp-nextstep-actions>button{flex:1 1 140px;justify-content:center}
                /* With header actions there are four buttons beside the avatar,
                   which left the name and room line ~80px, one word per line.
                   Give the buttons their own row above, right-aligned, so the
                   identity keeps full width. Only set when actions are passed. */
                [data-tp] .tp-head-row{flex-wrap:wrap;row-gap:12px}
                [data-tp] .tp-head-actions{order:-1;width:100%;justify-content:flex-end}
              }
            `}</style>
            <DialogPrimitive.Title
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              {name} — tenant profile
            </DialogPrimitive.Title>

            {banner}

            {/* header */}
            <Header
              name={name}
              initials={initials}
              avatarUrl={profile?.avatar_url ?? null}
              status={st}
              stepText={journey.stepText}
              archived={!!booking.admin_archived_at}
              archiveReason={booking.admin_archive_reason ?? null}
              moveOutRequested={!!moveOutDateRequest}
              roomTitle={roomTitle}
              propertyTitle={propertyTitle}
              expanded={expanded}
              isSuperAdmin={isSuperAdmin}
              onToggleExpand={() => setExpanded((v) => !v)}
              onClose={() => onOpenChange(false)}
              onToggleEdit={() => setEditOpen((v) => !v)}
              onToggleNotes={() => setNotesDrawerOpen((v) => !v)}
              openNoteCount={openNoteCount}
              editOpen={editOpen}
              tab={tab}
              onTab={changeTab}
              backLabel={backLabel}
              actions={headerActions}
            />

            {/* scroll body */}
            <div
              className="tp-scroll"
              style={{ flex: 1, overflowY: "auto", background: "#FCFCFD" }}
            >
              {nextAction && (
                <NextActionBar
                  hint={nextAction.hint}
                  label={nextAction.label}
                  disabled={busy}
                  onClick={nextAction.onClick}
                  secondaryLabel={
                    journey.stageId === "applied" ||
                    journey.stageId === "signature_pending"
                      ? t(
                          "admin.tenantProfile.actionUploadSignedPdf",
                          "Upload signed PDF"
                        )
                      : undefined
                  }
                  onSecondary={() => contractInputRef.current?.click()}
                />
              )}
              <input
                ref={contractInputRef}
                type="file"
                accept=".pdf,application/pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  pickUpload(e.currentTarget.files?.[0], "contract");
                  // Reset so picking the SAME file again still fires onChange
                  // (the contract picker is now reachable from several places).
                  e.currentTarget.value = "";
                }}
              />
              <input
                ref={proofInputRef}
                type="file"
                accept=".pdf,image/jpeg,image/png,image/webp"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.currentTarget.files?.[0];
                  if (f) void uploadProofFile(f);
                }}
              />

              {/* Inline error (toasts sit behind the overlay) */}
              {actionErr && (
                <div
                  style={{
                    margin: "18px 28px 0",
                    padding: "12px 16px",
                    borderRadius: 14,
                    background: "#FEECEC",
                    border: "1px solid #F7CFCF",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#B91C1C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0, marginTop: 1 }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4M12 16h.01" />
                  </svg>
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#991B1B",
                      lineHeight: 1.45,
                    }}
                  >
                    Action failed: {actionErr}
                  </div>
                  <button
                    onClick={() => setActionErr(null)}
                    title={t("admin.tenantProfile.actionDismiss", "Dismiss")}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#B91C1C",
                      cursor: "pointer",
                      display: "flex",
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Persistent Undo (toasts sit behind the overlay). Anchored
                  undos (e.g. move-in) render next to their own section inside
                  the tab body instead, so this is skipped for those. */}
              {lastUndo && !lastUndo.anchor && (
                <UndoBanner
                  label={lastUndo.label}
                  onUndo={() => void runUndo(lastUndo.fn)}
                  onDismiss={() => setLastUndo(null)}
                  busy={busy}
                  style={{ margin: "18px 28px 0" }}
                />
              )}

              <div
                className="tp-body"
                style={{
                  padding: "24px 28px 40px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                }}
              >
                {canManageTenant && editOpen && (
                  <TenantEditPanel
                    booking={booking as BookingRow}
                    profile={(profile as any) ?? null}
                    isSuperAdmin={isSuperAdmin}
                    currentUserId={currentUserId}
                    currentStageId={journey.stageId}
                    stageBusy={busy}
                    onForceStage={(s) => void forceStage(s)}
                    onSaved={refreshAfterWrite}
                    onClose={() => setEditOpen(false)}
                  />
                )}

                {tab === "overview" && (
                  <OverviewTab
                    key={`overview-${openNonce}`}
                    name={name}
                    email={email}
                    phone={phone}
                    dob={profile?.date_of_birth ?? null}
                    gender={profile?.gender ?? null}
                    nationality={profile?.nationality ?? null}
                    propertyTitle={propertyTitle}
                    roomTitle={roomTitle}
                    propertyId={reassignPropertyId}
                    roomId={reassignRoomId}
                    address={address}
                    booking={viewBooking}
                    depositPaid={viewDepositPaid}
                    journey={journey}
                    payments={payments}
                    proofByPayment={proofByPayment}
                    documents={documents}
                    documentsFailed={documentsFailed}
                    room={roomWithKeynest}
                    moveInProtocol={moveInProtocol}
                    moveOutProtocol={moveOutProtocol}
                    issues={issues}
                    issueComments={issueComments}
                    isSuperAdmin={isSuperAdmin}
                    archived={archived}
                    busy={busy}
                    onArchiveToggle={() => void archiveBooking()}
                    paymentsActivity={paymentsActivity}
                    depositPaidAt={depositPaidAt}
                    depositStatementSentAt={
                      depositSettlement?.settlement?.statement_sent_at ?? null
                    }
                    adminNotes={adminNotes}
                    auditLogs={auditLogs}
                    paymentNotes={paymentNotes}
                    loading={documentsLoading}
                    onGoTab={changeTab}
                    canGenerateContract={canGenerateContract}
                    canSendContract={canSendContract}
                    onGenerateContract={(templateId, mode) =>
                      void generateContract(templateId, mode)
                    }
                    onSendContract={() => void sendContractToTenant()}
                    onSaveContractInternalNote={
                      isSuperAdmin ? saveContractInternalNote : undefined
                    }
                    contractInternalNote={contractInternalNote ?? null}
                    onVerifyContractDraft={verifyContractDraft}
                    propertyDefaultTemplateId={
                      tenant?.property?.default_contract_template_id ?? null
                    }
                    onMarkContractSigned={() => void markContractSigned()}
                    onUploadSignedContract={() =>
                      contractInputRef.current?.click()
                    }
                  />
                )}
                {tab === "payments" && (
                  <PaymentsTab
                    key={`payments-${openNonce}`}
                    booking={viewBooking}
                    payments={payments}
                    receiptsByPayment={receiptsByPayment}
                    depositPaid={viewDepositPaid}
                    depositPaidAt={depositPaidAt}
                    sepa={sepa}
                    stageId={journey.stageId}
                    busy={busy}
                    proofByPayment={proofByPayment}
                    bookingId={bookingId}
                    userId={userId}
                    paymentsActivity={paymentsActivity}
                    noteCountByPayment={noteCountByPayment}
                    canManage={canManageTenant}
                    onMarkDepositPaid={() => void markDepositPaid()}
                    onViewProof={viewProof}
                    onUploadProof={pickProof}
                    onUploadDepositProof={() => void pickDepositProof()}
                    onDeleteProof={(pr) => setProofToDelete(pr)}
                    onEditPayment={editPayment}
                    onCreateScheduledRent={createScheduledRent}
                    onSetFirstMonthOverride={setFirstMonthOverride}
                    onSetRentDueDay={setRentDueDay}
                    onSendReminder={requestSendReminder}
                    onChargeSepa={(p) => setSepaChargeRow(p)}
                    onDecideDdCancellation={(requestId, decision) => {
                      // Rejecting is reversible (tenant can re-file); ending a
                      // mandate is not — that one gets an in-app confirm.
                      if (decision === "reject")
                        void decideDdCancellation(requestId, "reject");
                      else setDdCancelApprove(requestId);
                    }}
                    loading={paymentsLoading}
                  />
                )}
                {tab === "movein" && (
                  <MoveInTab
                    key={`movein-${openNonce}`}
                    booking={viewBooking}
                    journey={journey}
                    room={roomWithKeynest}
                    email={email}
                    tenantName={name}
                    isSuperAdmin={isSuperAdmin}
                    depositPaidAt={depositPaidAt}
                    busy={busy}
                    moveInProtocolSubmitted={moveInProtocolSubmitted}
                    moveInProtocol={moveInProtocol}
                    onOpenProtocolPdf={(kind) => void openProtocolPdf(kind)}
                    onRecordProtocol={
                      canManageTenant
                        ? (kind) => setRecordProtocolKind(kind)
                        : undefined
                    }
                    onReplaceProtocol={
                      canManageTenant
                        ? (kind) => setReplaceProtocolKind(kind)
                        : undefined
                    }
                    onMarkKeyPickedUp={(d) => void markKeyPickedUp(d)}
                    canManage={canManageTenant}
                    onMarkMoveInComplete={() =>
                      setSubmitOnBehalfKind("move_in")
                    }
                    onReopenMoveInProtocol={() => void reopenMoveInProtocol()}
                    onCloseMoveInProtocol={() => void closeMoveInProtocol()}
                    onUploadKeyDoc={(file) => pickUpload(file, "key-pickup")}
                    onSaveKeyNote={saveKeyNote}
                    adminNotes={adminNotes}
                    canGenerateContract={canGenerateContract}
                    canSendContract={canSendContract}
                    onGenerateContract={(templateId, mode) =>
                      void generateContract(templateId, mode)
                    }
                    onSendContract={() => void sendContractToTenant()}
                    onSaveContractInternalNote={
                      isSuperAdmin ? saveContractInternalNote : undefined
                    }
                    contractInternalNote={contractInternalNote ?? null}
                    onVerifyContractDraft={verifyContractDraft}
                    propertyDefaultTemplateId={
                      tenant?.property?.default_contract_template_id ?? null
                    }
                    onMarkContractSigned={() => void markContractSigned()}
                    onUploadSignedContract={() =>
                      contractInputRef.current?.click()
                    }
                    undo={
                      lastUndo?.anchor === "movein"
                        ? {
                            label: lastUndo.label,
                            onUndo: () => void runUndo(lastUndo.fn),
                            onDismiss: () => setLastUndo(null),
                          }
                        : null
                    }
                  />
                )}
                {tab === "moveout" && (
                  <MoveOutTab
                    key={`moveout-${openNonce}`}
                    booking={viewBooking}
                    journey={journey}
                    room={roomWithKeynest}
                    email={email}
                    busy={busy}
                    moveOutProtocolSubmitted={moveOutProtocolSubmitted}
                    moveOutProtocol={moveOutProtocol}
                    canManage={canManageTenant}
                    onInitiate={() => void initiateMoveOut()}
                    onReopenMoveOutProtocol={() => void reopenMoveOutProtocol()}
                    onCloseMoveOutProtocol={() => void closeMoveOutProtocol()}
                    onMarkMoveOutComplete={() =>
                      setSubmitOnBehalfKind("move_out")
                    }
                    onConfirmDate={(d) => void confirmMoveOutDate(d)}
                    onMarkKeyReturned={(d) => void markKeyReturned(d)}
                    onUploadKeyDoc={(file) => pickUpload(file, "key-return")}
                    onSaveKeyNote={saveKeyNote}
                    onReassign={
                      canReassign ? () => setReassignOpen(true) : undefined
                    }
                    adminNotes={adminNotes}
                    moveInProtocol={moveInProtocol}
                    settlement={depositSettlement?.settlement ?? null}
                    deductions={depositSettlement?.deductions ?? []}
                    settlementSchemaReady={
                      depositSettlement?.schemaReady ?? true
                    }
                    depositHeldCents={depositState.paidCents}
                    depositExpectedCents={depositState.expectedCents}
                    onAssembleBundle={() => void runAssembleBundle()}
                    onOpenBundle={() => void openBundle()}
                    onDownloadBundle={() => void downloadBundle()}
                    onDepositReleased={onDepositReleased}
                    onDepositLog={(event) => void logDepositEvent(event)}
                    onSendBundle={openSendBundleDialog}
                    onSettleDeposit={() => setSettleOpen(true)}
                    onMarkRefundPaid={() => void runMarkRefundPaid()}
                    onOpenStatement={() => void openStatement()}
                    onGenerateProtocolPdf={(kind) =>
                      void generateProtocolPdfFor(kind)
                    }
                    onOpenProtocolPdf={(kind) => void openProtocolPdf(kind)}
                    onRecordProtocol={
                      canManageTenant
                        ? (kind) => setRecordProtocolKind(kind)
                        : undefined
                    }
                    onReplaceProtocol={
                      canManageTenant
                        ? (kind) => setReplaceProtocolKind(kind)
                        : undefined
                    }
                    onDownloadProtocolPdf={(kind) =>
                      void downloadProtocolPdfFor(kind)
                    }
                    generatingProtocol={generatingProtocol}
                    markingRefundPaid={markingRefundPaid}
                    assemblingBundle={assembleBundle.isPending}
                    moveOutDateRequest={moveOutDateRequest ?? null}
                    moveOutDateRequestedByTenantAt={
                      latestMoveOutDateRequest?.status === "approved"
                        ? latestMoveOutDateRequest.created_at
                        : null
                    }
                    undo={
                      lastUndo?.anchor === "moveout"
                        ? {
                            label: lastUndo.label,
                            onUndo: () => void runUndo(lastUndo.fn),
                            onDismiss: () => setLastUndo(null),
                          }
                        : null
                    }
                    onDecideMoveOutRequest={(id, decision) => {
                      if (decision === "approve") {
                        setMoveOutRequestConfirm(id);
                      } else {
                        setMoveOutRequestReject(id);
                      }
                    }}
                  />
                )}
                {tab === "documents" && (
                  <DocumentsTab
                    key={`documents-${openNonce}`}
                    documents={documents}
                    filedDocuments={filedDocuments}
                    booking={viewBooking}
                    issueComments={issueComments}
                    adminNotes={adminNotes}
                    auditLogs={auditLogs}
                    busy={busy}
                    canUpload={!!(bookingId || userId)}
                    onUploadDocument={(file, docType) =>
                      void uploadDocument(file, docType)
                    }
                    onRenameDocument={(id, name) =>
                      void renameDocument(id, name)
                    }
                    onRenameNoteAttachment={(noteId, name) =>
                      void renameNoteAttachment(noteId, name)
                    }
                    onSaveDocumentNote={(id, note) =>
                      void saveDocumentNote(id, note)
                    }
                    onDeleteDocument={(doc, reason) =>
                      void deleteDocument(doc, reason)
                    }
                    onEditDocumentLabel={(id, docType) =>
                      void editDocumentLabel(id, docType)
                    }
                    customLabels={customDocLabels}
                    onCreateLabel={(label) => createDocLabel.mutateAsync(label)}
                    loading={documentsLoading}
                  />
                )}
              </div>
            </div>

            {/* Floating Edit (bottom-right) — matches the prototype's pill */}
            {canManageTenant && (
              <button
                onClick={() => setEditOpen((v) => !v)}
                title={
                  editOpen
                    ? t("admin.tenantProfile.actionCloseEditor", "Close editor")
                    : t("admin.tenantProfile.actionEditTenant", "Edit tenant")
                }
                style={{
                  position: "absolute",
                  right: 20,
                  bottom: 18,
                  zIndex: 20,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "11px 18px",
                  borderRadius: 999,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "#fff",
                  background: editOpen ? "#0F172A" : "#C0714F",
                  boxShadow: "0 10px 26px -10px rgba(192,113,79,.7)",
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                </svg>
                {editOpen
                  ? t("admin.tenantProfile.actionClose", "Close")
                  : "Edit"}
              </button>
            )}

            {/* Mandatory-note modal for any handover upload */}
            {pendingUpload && (
              <UploadNoteModal
                fileName={pendingUpload.file.name}
                title={
                  pendingUpload.kind === "contract"
                    ? t(
                        "admin.tenantProfile.noteSignedContract",
                        "Signed contract — handover note"
                      )
                    : pendingUpload.kind === "key-pickup"
                      ? t(
                          "admin.tenantProfile.noteKeyPickup",
                          "Key pickup — handover note"
                        )
                      : t(
                          "admin.tenantProfile.noteKeyReturn",
                          "Key return — handover note"
                        )
                }
                busy={busy}
                onSubmit={(note) => void confirmPendingUpload(note)}
                onCancel={() => {
                  setPendingUpload(null);
                  if (contractInputRef.current)
                    contractInputRef.current.value = "";
                }}
              />
            )}

            {/* File a protocol on the tenant's behalf — opened from either the
                Move-in / Move-out protocol card or the blocked deposit card. */}
            {canManageTenant && recordProtocolKind && (
              <RecordProtocolDialog
                open
                onOpenChange={(next) => {
                  if (!next) setRecordProtocolKind(null);
                }}
                kind={recordProtocolKind}
                tenantName={name}
                tenantEmail={email || null}
                busy={busy || recordProtocol.isPending}
                repairing={
                  recordProtocolKind === "move_in"
                    ? !!moveInProtocol &&
                      moveInProtocol.status === "submitted" &&
                      !moveInProtocol.pdf_storage_path
                    : !!moveOutProtocol &&
                      moveOutProtocol.status === "submitted" &&
                      !moveOutProtocol.pdf_storage_path
                }
                // The tenant's own signed submission stands untouched; this
                // files an ADDITIONAL protocol alongside it (never a replace —
                // only an operator's own recording can be replaced).
                additionalAlongsideTenant={
                  recordProtocolKind === "move_in"
                    ? !!moveInProtocol?.pdf_storage_path &&
                      !isAdminRecorded(moveInProtocol)
                    : !!moveOutProtocol?.pdf_storage_path &&
                      !isAdminRecorded(moveOutProtocol)
                }
                onSubmit={(payload) =>
                  void runRecordProtocol(recordProtocolKind, payload)
                }
              />
            )}

            {/* Swap the document on a protocol an operator already recorded —
                opened from the Move-in / Move-out protocol card. */}
            {canManageTenant && replaceProtocolKind && replaceTarget && (
              <RecordProtocolDialog
                open
                replacing
                bundleState={bundleStateOf(depositSettlement?.settlement)}
                initialNote={replaceTarget.admin_note ?? null}
                initialWalkthroughDate={replaceTarget.submitted_at ?? null}
                onOpenChange={(next) => {
                  if (!next) setReplaceProtocolKind(null);
                }}
                kind={replaceProtocolKind}
                tenantName={name}
                tenantEmail={email || null}
                busy={busy || recordProtocol.isPending}
                onSubmit={(payload) =>
                  void runRecordProtocol(replaceProtocolKind, payload, {
                    id: replaceTarget.id,
                    before: {
                      ref: replaceTarget.ref ?? null,
                      note: replaceTarget.admin_note ?? null,
                      walkthrough_date: replaceTarget.submitted_at ?? null,
                      recorded_by_name: replaceTarget.recorded_by_name ?? null,
                      recorded_at: replaceTarget.recorded_at ?? null,
                    },
                  })
                }
              />
            )}

            {/* Reassign / move room — opened from the Move-out tab */}
            {canReassign && reassignRoomId && reassignPropertyId && (
              <ReassignRoomDialog
                open={reassignOpen}
                onOpenChange={setReassignOpen}
                initialBookingId={bookingId!}
                initialTenantName={name}
                initialFromRoomId={reassignRoomId}
                propertyId={reassignPropertyId}
              />
            )}

            {/* Email preview before the completion notice goes to the owner —
                opened from the Move-out tab's deposit return card. */}
            <SendBundleDialog
              open={sendBundleOpen}
              onOpenChange={setSendBundleOpen}
              preview={previewBundleEmail.data?.preview ?? null}
              loading={previewBundleEmail.isPending}
              sending={sendBundle.isPending}
              error={
                previewBundleEmail.error instanceof Error
                  ? previewBundleEmail.error.message
                  : null
              }
              recipient={bundleRecipient}
              onRecipientChange={setBundleRecipient}
              previewStale={
                !!bundleRecipient.trim() &&
                previewedRecipient !== bundleRecipient.trim()
              }
              warning={previewBundleEmail.data?.warning ?? null}
              onSend={() => void submitSendBundle()}
              bundlePageCount={
                depositSettlement?.settlement?.bundle_page_count ?? null
              }
              bundleSizeBytes={
                depositSettlement?.settlement?.bundle_size_bytes ?? null
              }
            />

            {/* Record a phone decision — the same panel the representative sees,
                in English, logged under the admin's name. */}
            <SettleDepositDialog
              open={settleOpen}
              onOpenChange={setSettleOpen}
              settlementId={depositSettlement?.settlement?.id ?? null}
              onReleased={onDepositReleased}
              onLog={(event) => void logDepositEvent(event)}
            />

            {/* File a protocol on the tenant's behalf — no signature. */}
            <SubmitProtocolOnBehalfDialog
              open={submitOnBehalfKind !== null}
              onOpenChange={(o) => {
                if (!o) setSubmitOnBehalfKind(null);
              }}
              kind={submitOnBehalfKind}
              busy={busy}
              onSubmit={(args) =>
                submitOnBehalfKind
                  ? submitProtocolOnBehalf(submitOnBehalfKind, args)
                  : Promise.resolve(false)
              }
            />

            {/* Internal notes, over the profile rather than beside it. Last
                child of the frame so it layers above every tab. */}
            <NotesDrawer
              open={notesDrawerOpen}
              onClose={() => setNotesDrawerOpen(false)}
              scope={{ level: "tenant", bookingId, userId }}
              emptyHint={t(
                "admin.tenantProfile.notesPlaceholder",
                "Anything the next admin should know about this tenant — calls, verbal agreements, open follow-ups."
              )}
            />
          </div>

          {/* Dunning-reminder confirmation — replaces the native confirm(). */}
          <AlertDialog
            open={reminderStage !== null}
            onOpenChange={(o) => {
              if (!o) setReminderStage(null);
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t(
                    "admin.tenantProfile.confirmDunningTitle",
                    "Send dunning reminder?"
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {reminderStage !== null
                    ? `This sends dunning reminder stage ${reminderStage} to the tenant, escalates the booking's dunning stage, and emails them.`
                    : ""}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>
                  {t("admin.tenantProfile.actionCancel", "Cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy}
                  onClick={() => {
                    const stage = reminderStage;
                    setReminderStage(null);
                    if (stage !== null) void sendReminder(stage);
                  }}
                >
                  {t(
                    "admin.tenantProfile.actionSendNextReminder",
                    "Send next reminder"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Direct-debit collection — moves real money, so confirm in-app (5a). */}
          <AlertDialog
            open={sepaChargeRow !== null}
            onOpenChange={(o) => {
              if (!o) setSepaChargeRow(null);
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t(
                    "admin.tenantProfile.confirmDebitTitle",
                    "Collect via direct debit?"
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {sepaChargeRow
                    ? `${t("admin.tenantProfile.confirmDebitDescPrefix", "This debits")} €${((sepaChargeRow.amount_cents || 0) / 100).toLocaleString(language === "de" ? "de-DE" : "en-GB", { minimumFractionDigits: 2 })} ${t("admin.tenantProfile.confirmDebitDescMid", "from the tenant's bank account")}${
                        sepa?.ibanLast4 ? ` (IBAN •••• ${sepa.ibanLast4})` : ""
                      } ${t("admin.tenantProfile.confirmDebitDescSuffix", "under their SEPA mandate. A pre-notification email is sent first; settlement takes 2–5 business days.")}`
                    : ""}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>
                  {t("admin.tenantProfile.cancel", "Cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy}
                  onClick={() => {
                    const row = sepaChargeRow;
                    setSepaChargeRow(null);
                    if (row) void chargeSepa(String(row.id));
                  }}
                >
                  {t("admin.tenantProfile.actionCollectNow", "Collect now")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Ending a SEPA mandate — irreversible, so confirm in-app (6). */}
          <AlertDialog
            open={ddCancelApprove !== null}
            onOpenChange={(o) => {
              if (!o) setDdCancelApprove(null);
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t(
                    "admin.tenantProfile.confirmEndMandateTitle",
                    "End the SEPA mandate?"
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "admin.tenantProfile.confirmEndMandateDesc",
                    "This approves the tenant's request: the Stripe payment method is detached, the mandate is revoked, and no further rent can be debited. The tenant switches to their requested method and would have to sign a brand-new mandate to use direct debit again."
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>
                  {t("admin.tenantProfile.cancel", "Cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy}
                  onClick={() => {
                    const id = ddCancelApprove;
                    setDdCancelApprove(null);
                    if (id) void decideDdCancellation(id, "approve");
                  }}
                >
                  {t("admin.tenantProfile.actionEndMandate", "End mandate")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Checkout protocol: confirming a tenant's requested move-out
              date — Neel's "sure you want to confirm..." popup. */}
          <AlertDialog
            open={moveOutRequestConfirm !== null}
            onOpenChange={(o) => {
              if (!o) setMoveOutRequestConfirm(null);
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t(
                    "admin.tenantTabs.moveOut.confirmTitle",
                    "Confirm move-out on {date}?"
                  ).replace(
                    "{date}",
                    moveOutDateRequest
                      ? formatDateNumeric(
                          moveOutDateRequest.requested_date,
                          language
                        )
                      : ""
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription style={{ whiteSpace: "pre-line" }}>
                  {t(
                    "admin.tenantTabs.moveOut.confirmDesc",
                    "Are you sure you want to confirm {name}'s requested move-out date?\n\nThis turns on the move-out sequence with {date} as the confirmed date and sends {firstName} the move-out confirmation email immediately."
                  )
                    .replace("{name}", name || email)
                    .replace(
                      /\{date\}/g,
                      moveOutDateRequest
                        ? formatDateNumeric(
                            moveOutDateRequest.requested_date,
                            language
                          )
                        : ""
                    )
                    .replace("{firstName}", (name || email).split(" ")[0])}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>
                  {t("admin.tenantProfile.cancel", "Cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy}
                  onClick={() => {
                    const id = moveOutRequestConfirm;
                    const date = moveOutDateRequest?.requested_date;
                    setMoveOutRequestConfirm(null);
                    if (id && date) void approveMoveOutRequest(id, date);
                  }}
                >
                  {t(
                    "admin.tenantTabs.moveOut.confirmAction",
                    "Yes, confirm move-out"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Checkout protocol: rejecting opens the shared reason-chip
              composer — the SAME one the Brain card uses (BrainCardActions.tsx),
              so the two surfaces can never drift apart. */}
          <MoveOutRejectComposer
            open={moveOutRequestReject !== null}
            onOpenChange={(o) => {
              if (!o) setMoveOutRequestReject(null);
            }}
            requestId={moveOutRequestReject}
            requestedDate={moveOutDateRequest?.requested_date ?? null}
            tenantEmail={email}
            tenantName={name || email}
            onRejected={() => {
              queryClient.invalidateQueries({
                queryKey: ["admin.move-out-request", bookingId],
              });
              queryClient.invalidateQueries({
                queryKey: ["admin.move-out-request.latest", bookingId],
              });
            }}
          />

          {/* Payment-proof deletion — destructive in-app confirm. */}
          <AlertDialog
            open={proofToDelete !== null}
            onOpenChange={(o) => {
              if (!o) setProofToDelete(null);
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t(
                    "admin.tenantProfile.confirmDeleteProofTitle",
                    "Delete payment proof?"
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "admin.tenantProfile.confirmDeleteProofDesc",
                    "The file is removed permanently and can't be recovered."
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>
                  {t("admin.tenantProfile.cancel", "Cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy}
                  className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600"
                  onClick={() => {
                    const proof = proofToDelete;
                    setProofToDelete(null);
                    if (proof) void deleteProof(proof);
                  }}
                >
                  {t("admin.tenantProfile.actionDeleteProof", "Delete proof")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* ================================================================== */
/* Upload note modal (mandatory handover note, ≥ MIN_WORDS words)      */
/* ================================================================== */

const MIN_WORDS = 5;
const wordCount = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

function UploadNoteModal({
  fileName,
  title,
  busy,
  onSubmit,
  onCancel,
}: {
  fileName: string;
  title: string;
  busy: boolean;
  onSubmit: (note: string) => void;
  onCancel: () => void;
}) {
  const { t } = useLanguage();
  const [note, setNote] = useState("");
  const words = wordCount(note);
  const enough = words >= MIN_WORDS;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(9,15,28,.55)",
        backdropFilter: "blur(2px)",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0 30px 80px -20px rgba(9,15,28,.5)",
          overflow: "hidden",
          fontFamily: "Inter,system-ui,-apple-system,'Segoe UI',sans-serif",
          color: "#0F172A",
        }}
      >
        <div style={{ padding: "20px 24px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <SectionIcon bg="#EAF1FE" color="#3B82F6" size={40}>
              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
            </SectionIcon>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "#94A3B8",
                  fontWeight: 500,
                  marginTop: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName}
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: "16px 24px 4px" }}>
          <div style={{ ...uppLabel, marginBottom: 6 }}>
            {t(
              "admin.tenantProfile.handoverNoteLabel",
              "Handover note (required)"
            )}
          </div>
          <textarea
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={`${t("admin.tenantProfile.handoverNotePlaceholderPrefix", "Describe the handover — condition, who was present, anything relevant. Minimum")} ${MIN_WORDS} ${t("admin.tenantProfile.wordsSuffix", "words…")}`}
            style={{
              width: "100%",
              minHeight: 120,
              border: `1px solid ${enough ? "#CDECE7" : "#E7E9EC"}`,
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 13.5,
              lineHeight: 1.5,
              background: "#fff",
              color: "#0F172A",
              resize: "vertical",
              fontFamily: "inherit",
              outline: "none",
            }}
          />
          <div
            style={{
              marginTop: 8,
              fontSize: 12.5,
              fontWeight: 600,
              color: enough ? "#0B7268" : "#94A3B8",
            }}
          >
            {enough
              ? `✓ ${words} ${t("admin.tenantProfile.wordsPlain", "words")}`
              : `${words} / ${MIN_WORDS} ${t("admin.tenantProfile.wordsPlain", "words")} — ${t("admin.tenantProfile.addMorePrefix", "add")} ${MIN_WORDS - words} ${t("admin.tenantProfile.moreToSubmitSuffix", "more to submit")}`}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "12px 24px 20px",
          }}
        >
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              border: "1px solid #E7E9EC",
              background: "#fff",
              color: "#64748B",
              borderRadius: 11,
              padding: "10px 16px",
              fontSize: 13.5,
              fontWeight: 600,
              cursor: busy ? "default" : "pointer",
            }}
          >
            {t("admin.tenantProfile.cancel", "Cancel")}
          </button>
          <button
            onClick={() => enough && !busy && onSubmit(note.trim())}
            disabled={!enough || busy}
            style={{
              border: "none",
              background: enough && !busy ? "#12B5A8" : "#CBD5E1",
              color: "#fff",
              borderRadius: 11,
              padding: "10px 18px",
              fontSize: 13.5,
              fontWeight: 700,
              cursor: enough && !busy ? "pointer" : "default",
            }}
          >
            {busy
              ? t("admin.tenantProfile.uploading", "Uploading…")
              : t("admin.tenantProfile.actionSubmitUpload", "Submit & upload")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/* Header                                                              */
/* ================================================================== */

function Header(props: {
  name: string;
  initials: string;
  avatarUrl: string | null;
  status: (typeof TENANT_JOURNEY_STAGES)[number];
  stepText: string;
  /**
   * `admin_archived_at` is set. Shown as its own chip beside the stage, never
   * instead of it: archiving hides a tenancy from the live pipeline, it does
   * not move it along the ladder, and the Tenants list / room history show it
   * the same way.
   */
  archived: boolean;
  /**
   * `admin_archive_reason` — why, when the SYSTEM archived it (COL-165). The
   * chip's fallback wording asserts an admin did it, which is false for a row
   * the signing cascade archived, so this has to reach every surface that
   * renders the chip, not just the Tenants list.
   */
  archiveReason?: string | null;
  /** A pending checkout-protocol request exists for this booking. */
  moveOutRequested?: boolean;
  roomTitle: string;
  propertyTitle: string;
  expanded: boolean;
  isSuperAdmin: boolean;
  editOpen: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  onToggleEdit: () => void;
  onToggleNotes: () => void;
  /** Unresolved threads on this tenant — the badge on the notes button. */
  openNoteCount: number;
  tab: TabId;
  onTab: (tabId: TabId) => void;
  backLabel?: string;
  actions?: React.ReactNode;
}) {
  const { t } = useLanguage();
  const { status: st } = props;
  const iconBtn: React.CSSProperties = {
    width: 38,
    height: 38,
    borderRadius: 11,
    border: "1px solid #E7E9EC",
    background: "#fff",
    color: "#475569",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  };
  return (
    <div
      className="tp-head"
      style={{
        padding: "24px 28px 0",
        background: "linear-gradient(180deg,#FBFAF8 0%,#fff 100%)",
        borderBottom: "1px solid #F0F1F3",
        flexShrink: 0,
      }}
    >
      {props.backLabel && (
        <button
          onClick={props.onClose}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "none",
            border: "none",
            padding: "0 0 12px",
            margin: 0,
            cursor: "pointer",
            color: "#64748B",
            fontSize: 13,
            fontWeight: 600,
          }}
          title={`${t("admin.tenantProfile.backToPrefix", "Back to")} ${props.backLabel}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          {props.backLabel}
        </button>
      )}
      <div
        className={props.actions ? "tp-head-row" : undefined}
        style={{ display: "flex", alignItems: "flex-start", gap: 16 }}
      >
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: "50%",
            background: props.avatarUrl
              ? `center/cover url(${props.avatarUrl})`
              : "linear-gradient(140deg,#3AA9F0,#12B5A8)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 20,
            boxShadow: "0 6px 16px -4px rgba(18,181,168,.5)",
            flexShrink: 0,
          }}
        >
          {!props.avatarUrl && props.initials}
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: 25,
                fontWeight: 800,
                letterSpacing: "-.025em",
                lineHeight: 1.05,
              }}
            >
              {props.name}
            </h1>
            <span
              // The stage is derived, never typed in — say so on hover. This is
              // where the removed "status ahead" banner's explanation now
              // lives, in a neutral voice.
              title={t(
                "admin.tenantProfile.stageTooltip",
                "Stage is worked out from what is on file — contract → deposit → keys → move-in. Each step needs the one before it, so a booking status set ahead of the evidence does not move the tenant."
              )}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontSize: 13,
                fontWeight: 600,
                padding: "5px 13px 5px 11px",
                borderRadius: 999,
                background: st.bg,
                color: st.text,
                border: `1px solid ${st.border}`,
                cursor: "help",
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: st.dot,
                  boxShadow: `0 0 0 3px ${st.ring}`,
                  animation:
                    st.id === "active"
                      ? "tpActivePulse 1.8s ease-in-out infinite"
                      : undefined,
                }}
              />
              {tenantJourneyStageLabel(t, st.id)}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#94A3B8" }}>
              {props.stepText}
            </span>
            {props.archived && (
              <TenantArchivedChip reason={props.archiveReason} />
            )}
            {props.moveOutRequested && (
              <span style={CHIP.amber2}>
                {t(
                  "admin.tenantTabs.moveOut.headerBadge",
                  "Move-out requested"
                )}
              </span>
            )}
          </div>
          <p
            style={{
              margin: "6px 0 0",
              fontSize: 14,
              color: "#64748B",
              fontWeight: 500,
            }}
          >
            {props.roomTitle} · {props.propertyTitle}
          </p>
        </div>
        <div
          className={props.actions ? "tp-head-actions" : undefined}
          style={{ display: "flex", gap: 8, flexShrink: 0 }}
        >
          {props.actions}
          {/* Notes live over the profile rather than in a sixth tab: they are
              what you want open WHILE reading the payments or the move-out. */}
          <button
            onClick={props.onToggleNotes}
            title={t(
              "admin.tenantProfile.sectionInternalNotes",
              "Internal notes"
            )}
            aria-label={
              props.openNoteCount > 0
                ? `${t("admin.tenantProfile.sectionInternalNotes", "Internal notes")} — ${props.openNoteCount} ${t("admin.tenantProfile.openSuffix", "open")}`
                : t(
                    "admin.tenantProfile.sectionInternalNotes",
                    "Internal notes"
                  )
            }
            style={{ ...iconBtn, position: "relative" }}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              <rect x="8" y="2" width="8" height="4" rx="1" />
              <path d="M8 11h8M8 15h5" />
            </svg>
            {props.openNoteCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -5,
                  right: -5,
                  minWidth: 18,
                  height: 18,
                  padding: "0 5px",
                  borderRadius: 9,
                  background: "#0F4C4A",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 800,
                  lineHeight: "18px",
                  border: "2px solid #fff",
                }}
              >
                {props.openNoteCount > 99 ? "99+" : props.openNoteCount}
              </span>
            )}
          </button>
          <button
            onClick={props.onToggleExpand}
            title={t("admin.tenantProfile.actionExpand", "Expand")}
            style={iconBtn}
          >
            {props.expanded ? (
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3m13-5v3a2 2 0 0 1-2 2h-3" />
              </svg>
            ) : (
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
            )}
          </button>
          <button
            onClick={props.onClose}
            title={t("admin.tenantProfile.close", "Close")}
            style={iconBtn}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* tab bar */}
      <div
        className="tp-tabs"
        style={{
          display: "flex",
          gap: 4,
          marginTop: 20,
          background: "#F1F2F4",
          borderRadius: 14,
          padding: 5,
        }}
      >
        {TABS.map((tab) => {
          const active = props.tab === tab.id;
          const dot = tab.id === "moveout" && props.moveOutRequested;
          return (
            <button
              key={tab.id}
              onClick={() => props.onTab(tab.id)}
              style={{
                flex: 1,
                border: "none",
                cursor: "pointer",
                padding: "10px 8px",
                borderRadius: 10,
                fontSize: 13.5,
                fontWeight: 600,
                background: active ? "#fff" : "transparent",
                color: active ? "#0F172A" : "#64748B",
                boxShadow: active ? "0 1px 3px rgba(0,0,0,.08)" : "none",
                display: dot ? "flex" : undefined,
                alignItems: dot ? "center" : undefined,
                justifyContent: dot ? "center" : undefined,
                gap: dot ? 7 : undefined,
              }}
            >
              {t(tab.labelKey, tab.label)}
              {dot && (
                <span
                  aria-label={t(
                    "admin.tenantTabs.moveOut.tabDotLabel",
                    "Needs decision"
                  )}
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "#EA580C",
                    flexShrink: 0,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================== */
/* Next-step action bar                                                */
/* ================================================================== */

/*
 * The "Booking status says X, but the tenant is at Y" banner (StatusMismatchNotice)
 * used to sit here. Removed on request: the header pill and the Next-step bar
 * already say where the tenant really is and what is missing, so the banner
 * repeated it in a scarier voice on every manually-created tenant.
 *
 * Nothing about the rule changed — `resolveTenantJourney` still refuses to
 * promote a tenant past an unmet gate, and `journey.statusAhead` is still
 * computed (the Tenants CSV export reports it).
 */
function NextActionBar({
  hint,
  label,
  disabled,
  onClick,
  secondaryLabel,
  onSecondary,
}: {
  hint: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}) {
  const { t } = useLanguage();
  return (
    <div
      style={{
        margin: "22px clamp(16px, 4vw, 28px) 0",
        padding: "16px 18px",
        borderRadius: 18,
        background: "linear-gradient(135deg,#0B3B38,#12706A)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        // The buttons keep their intrinsic width (a wrapped "Mark contract
        // signed" is worse than a taller card), so the row has to be allowed to
        // wrap. Without this the hint column was crushed to one word per line
        // and the buttons ran outside a panel that clips its overflow.
        flexWrap: "wrap",
        gap: 16,
        rowGap: 14,
        boxShadow: "0 12px 30px -14px rgba(18,112,106,.7)",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: "rgba(255,255,255,.14)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#7FF0E6"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
        </svg>
      </div>
      {/* 260px basis: below that the hint takes the whole row and the actions
          drop underneath, rather than the hint being squeezed to nothing. */}
      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".09em",
            textTransform: "uppercase",
            color: "#7FF0E6",
          }}
        >
          {t("admin.tenantProfile.nextStep", "Next step")}
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>
          {hint}
        </div>
      </div>
      <div
        className="tp-nextstep-actions"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 10,
          // Right-aligned beside the hint on a wide card; a full-width row of
          // its own once the card wraps (see the phone rule in the panel CSS).
          marginLeft: "auto",
        }}
      >
        {secondaryLabel && onSecondary && (
          <button
            onClick={onSecondary}
            disabled={disabled}
            style={{
              background: "rgba(255,255,255,.12)",
              color: "#EAFBF8",
              border: "1px solid rgba(255,255,255,.28)",
              borderRadius: 11,
              padding: "11px 16px",
              fontWeight: 600,
              fontSize: 13,
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.7 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            {secondaryLabel}
          </button>
        )}
        <button
          onClick={onClick}
          disabled={disabled}
          style={{
            background: "#fff",
            color: "#0B3B38",
            border: "none",
            borderRadius: 11,
            padding: "11px 18px",
            fontWeight: 700,
            fontSize: 13.5,
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.7 : 1,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {label}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export type { TabId };
